import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, orderBy, onSnapshot, setDoc, serverTimestamp, FieldValue, runTransaction, Timestamp, DocumentReference, CollectionReference, limit, increment, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useSearchParams } from 'react-router-dom';
import { useIsMobile, useMediaQuery } from '../../../utils/useMediaQuery';
import { db, storage } from '../../../utils/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Teacher, User } from '../../../utils/firebase';
import { SUBJECTS } from '../../../utils/subjectUtils';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import SearchIcon from '../../../components/icons/SearchIcon';
import EditPencilIcon from '../../../components/icons/EditPencilIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import CheckCircleIcon from '../../../components/icons/CheckCircleIcon';
import QrCodeIcon from '../../../components/icons/QrCodeIcon';
import ToggleSwitch from '../../../components/ui/ToggleSwitch';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';
import PlusCircleIcon from '../../../components/icons/PlusCircleIcon';
import EditStoreNoticeDialog from '../../../components/ui/EditStoreNoticeDialog';
import EditStoreItemDialog from '../../../components/ui/EditStoreItemDialog';
import StoreItemCardForTeacher from '../../../components/ui/StoreItemCardForTeacher';
import VerticalImageCard from '../../../components/ui/VerticalImageCard';
import InputField from '../../../components/ui/InputField';
import DropdownButton from '../../../components/ui/DropdownButton';
import { FEATURE_CHARACTER_COSMETIC } from '../../../config/featureFlags';
import { PET_CONSUMABLE_CATEGORY, PET_PLAY_TICKET_CATEGORY, type PetItemEffect, type PetPlayActionType } from '../../../types/petStore';
import type { FurnitureKey, RoomSurfaceKey } from '../../../types/myHome';
import { appendClassStorePurchaseLog } from '../../../utils/storePurchaseLog';
import {
  countItemsByStoreType,
  getCategoryFilterOptions,
  getStoreTypeLabel,
  getStoreTypeVisual,
  getVisibleStoreTypes,
  itemBelongsToStoreType,
  parseStoreTypeParam,
  storeTypeToQueryValue,
  type StoreItemCategoryId,
  type StoreTypeId,
} from '../../../utils/storeTypes';

// User 인터페이스를 StoreUser로 이름 변경
interface StoreUser {
  id: string;
  name: string;
  email: string;
  role: string;
  schoolId?: string;
  classId?: string;
}

// Teacher 타입 정의
interface StoreTeacher {
  id: string;
  uid: string;
  name: string;
  email: string;
  role: 'teacher' | 'schoolAdmin' | 'superAdmin';
  schoolId: string;
  classId: string;
  subjectId?: string;
  subject?: keyof typeof SUBJECTS;  // subject를 SUBJECTS의 키 타입으로 제한
}

// 타입 가드 함수 수정
const isTeacher = (user: StoreUser): user is StoreTeacher => {
  return user.role === 'teacher' || user.role === 'superAdmin';
};

const canManageStore = (user: StoreUser | null): boolean => {
  if (!user) {
    console.log('canManageStore: user is null');
    return false;
  }
  console.log('canManageStore: user role:', user.role);
  return ['teacher', 'schoolAdmin', 'superAdmin'].includes(user.role);
};

interface StoreItem {
  id: string;
  name: string;
  description: string;
  price: number;
  targetType: 'class' | 'subject'; // 판매 대상: 학급/교과
  itemType: 'point' | 'stamp'; // 아이템 유형: 학급은 포인트, 교과는 도장으로 고정
  itemCategory: 'usage' | 'qr' | 'seatReservation' | 'seatTakeover' | 'collectible' | 'gacha' | 'cosmetic' | 'furniture' | 'roomSurface' | typeof PET_CONSUMABLE_CATEGORY | typeof PET_PLAY_TICKET_CATEGORY; // 아이템 종류
  petEffect?: PetItemEffect;
  petPlayAction?: PetPlayActionType;
  characterSlot?: string;
  characterOutfitId?: string;
  furnitureKey?: FurnitureKey;
  roomSurfaceKey?: RoomSurfaceKey;
  gachaItems?: Array<{ // 뽑기 아이템 목록
    itemId?: string; // 꽝인 경우 없음
    itemName: string;
    count: number; // 개수
    imageUrl?: string;
    isBlank?: boolean; // 꽝 여부
  }>;
  gachaAnimationType?: 'scratch' | 'card' | 'maru'; // 뽑기 애니메이션 타입
  usageType?: string; // 사용 유형: 먹을거리, 아이템, 물품 등 (설정에서 관리)
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date | FieldValue;
  stock: number;
  isLimited: boolean;
  salePeriod?: {
    start: Date;
    end: Date;
  };
  isUnlimitedStock: boolean;
  qrData?: string;
  imageUrl?: string;
  teacherId: string;
  classId?: string;
  subjectId?: string;
  subject?: string;
}

interface StudentItem {
  itemId: string;
  quantity: number;
  purchasedAt: Timestamp | null;
  lastUsedAt: Timestamp | null;
}

interface Student {
  id: string;
  name: string;
  email: string;
  role: string;
  points: number;
  items: {
    [key: string]: StudentItem;
  };
}

interface ClassStudentOption {
  id: string;
  name: string;
}

interface FormData {
  name: string;
  description: string;
  price: string;
  stock: string;
  isLimited: boolean;
  hasSalePeriod: boolean;
  salePeriodStart: string;
  salePeriodEnd: string;
  isUnlimitedStock: boolean;
  type: 'point' | 'stamp';
  noStockLimit: boolean;
}

interface StoreNotice {
  id: string;
  title: string;
  content: string;
  tags?: string[];
  startDate?: Date;
  endDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  targetType: 'class' | 'subject';
  teacherId: string;
  classId?: string;
  subjectId?: string;
}

/** 상점 기록 탭 — 학생 문서 items 맵을 펼친 한 줄 */
interface StudentHoldingRow {
  studentId: string;
  studentName: string;
  inventoryKey: string;
  storeItemId: string;
  itemName: string;
  quantity?: number;
  status?: string;
  purchasedAt: Date | null;
}

/** 학생+상품 기준으로 묶은 보유 행 */
interface StudentHoldingGroupedRow {
  studentId: string;
  studentName: string;
  itemName: string;
  storeItemId: string;
  /** 인벤토리 줄 수 합산(각 줄은 quantity 없으면 1건) */
  count: number;
  statusSummary?: string;
  lastPurchasedAt: Date | null;
}

function aggregateStudentHoldingsByStudentAndItem(rows: StudentHoldingRow[]): StudentHoldingGroupedRow[] {
  type Acc = {
    studentId: string;
    studentName: string;
    itemName: string;
    storeItemId: string;
    count: number;
    statuses: Set<string>;
    lastPurchasedAt: Date | null;
  };
  const map = new Map<string, Acc>();

  for (const r of rows) {
    const itemKey = (r.storeItemId && r.storeItemId.trim()) || r.itemName;
    const k = `${r.studentId}\0${itemKey}`;
    const piece = typeof r.quantity === 'number' && r.quantity > 0 ? r.quantity : 1;
    const prev = map.get(k);
    if (!prev) {
      map.set(k, {
        studentId: r.studentId,
        studentName: r.studentName,
        itemName: r.itemName,
        storeItemId: r.storeItemId,
        count: piece,
        statuses: new Set(r.status ? [String(r.status)] : []),
        lastPurchasedAt: r.purchasedAt,
      });
    } else {
      prev.count += piece;
      if (r.status) prev.statuses.add(String(r.status));
      if (r.purchasedAt) {
        if (!prev.lastPurchasedAt || r.purchasedAt.getTime() > prev.lastPurchasedAt.getTime()) {
          prev.lastPurchasedAt = r.purchasedAt;
        }
      }
    }
  }

  return [...map.values()]
    .map((v) => ({
      studentId: v.studentId,
      studentName: v.studentName,
      itemName: v.itemName,
      storeItemId: v.storeItemId,
      count: v.count,
      statusSummary: v.statuses.size ? [...v.statuses].join(', ') : undefined,
      lastPurchasedAt: v.lastPurchasedAt,
    }))
    .sort((a, b) => {
      const tb = b.lastPurchasedAt?.getTime() ?? 0;
      const ta = a.lastPurchasedAt?.getTime() ?? 0;
      if (tb !== ta) return tb - ta;
      const n = a.studentName.localeCompare(b.studentName, 'ko');
      if (n !== 0) return n;
      return a.itemName.localeCompare(b.itemName, 'ko');
    });
}

/** classes/{classId}/purchases 문서 (appendClassStorePurchaseLog 스키마) */
interface ClassPurchaseDoc {
  id: string;
  studentId?: string;
  studentName?: string;
  storeItemId?: string;
  itemName?: string;
  price?: number;
  currency?: string;
  itemType?: string;
  targetType?: string;
  subjectId?: string;
  itemCategory?: string;
  inventoryKey?: string;
  selectedGachaItem?: { itemName?: string; itemId?: string; isBlank?: boolean };
  createdAt?: Timestamp;
}

// Timestamp를 Date로 변환하는 함수 추가
const convertTimestampToDate = (value: Timestamp | Date | any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  // 이미 Date인 경우
  return new Date(value);
};

// Date를 Timestamp로 변환하는 함수 추가
const convertDateToTimestamp = (date: Date): Timestamp => {
  return Timestamp.fromDate(date);
};


// User를 Teacher로 변환하는 함수 수정
const convertUserToTeacher = (user: User | null): StoreTeacher | null => {
  if (!user) return null;
  if (user.role === 'student') return null;
  
  return {
    id: user.uid,
    uid: user.uid,
    name: user.name,
    email: user.email,
    role: user.role,
    schoolId: user.schoolId,
    classId: user.classId || '',
    subjectId: user.subjectId
  };
};

const StoreManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const isHubCompact = useMediaQuery('(max-width: 768px)');
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const teacher = currentUser as Teacher;
  
  const [currentTeacher, setCurrentTeacher] = useState<StoreTeacher | null>(null);
  const [items, setItems] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // 탭 상태 (공지사항/상점 아이템/일괄 지급/상점 기록/설정)
  const [activeTab, setActiveTab] = useState<'notice' | 'items' | 'bulkGrant' | 'settings' | 'records'>('items');
  const [recordsSubTab, setRecordsSubTab] = useState<'holdings' | 'purchases'>('holdings');
  /** '' = 전체, 그 외 = studentId */
  const [recordsStudentIdFilter, setRecordsStudentIdFilter] = useState('');
  /** '' = 전체, 그 외 = 아이템명(정확 일치) */
  const [recordsItemNameFilter, setRecordsItemNameFilter] = useState('');
  const [studentHoldings, setStudentHoldings] = useState<StudentHoldingRow[]>([]);
  const [purchaseRows, setPurchaseRows] = useState<ClassPurchaseDoc[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState<string | null>(null);
  
  // 아이템 관련 상태
  const [searchTerm, setSearchTerm] = useState('');
  /** null = 상점 선택(허브) 화면 */
  const [selectedStoreType, setSelectedStoreType] = useState<StoreTypeId | null>(() =>
    parseStoreTypeParam(searchParams.get('store'))
  );
  const [selectedCategory, setSelectedCategory] = useState<'all' | StoreItemCategoryId>('all');
  const [selectedStock, setSelectedStock] = useState<'all' | 'inStock' | 'outOfStock'>('all');
  const [selectedActiveStatus, setSelectedActiveStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [classStudents, setClassStudents] = useState<ClassStudentOption[]>([]);
  const [grantTargetStudentId, setGrantTargetStudentId] = useState('');
  const [grantingItemId, setGrantingItemId] = useState<string | null>(null);
  const [isGrantDialogOpen, setIsGrantDialogOpen] = useState(false);
  const [grantItem, setGrantItem] = useState<StoreItem | null>(null);
  const [bulkGrantItemId, setBulkGrantItemId] = useState('');
  const [bulkGrantStudentIds, setBulkGrantStudentIds] = useState<string[]>([]);
  const [bulkGrantInProgress, setBulkGrantInProgress] = useState(false);
  
  // 페이지네이션
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  
  // 다이얼로그 상태
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StoreItem | null>(null);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [itemDialogMode, setItemDialogMode] = useState<'create' | 'edit'>('create');
  
  // 공지사항 관련 상태
  const [notices, setNotices] = useState<StoreNotice[]>([]);
  const [openNoticeDialog, setOpenNoticeDialog] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<StoreNotice | null>(null);
  const [isNoticeEditDialogOpen, setIsNoticeEditDialogOpen] = useState(false);
  const [noticeDialogMode, setNoticeDialogMode] = useState<'create' | 'edit'>('create');
  
  // 설정 관련 상태
  const [itemCategories, setItemCategories] = useState<Array<{ id: string; label: string; value: string }>>([
    { id: 'usage', label: '사용권', value: 'usage' },
    { id: 'qr', label: 'QR 코드', value: 'qr' },
    { id: 'seatReservation', label: '좌석 예약권', value: 'seatReservation' },
    { id: 'seatTakeover', label: '좌석 강탈권', value: 'seatTakeover' },
    { id: 'collectible', label: '소장용', value: 'collectible' }
  ]);
  const [usageTypes, setUsageTypes] = useState<Array<{ id: string; label: string; imageUrl?: string }>>([
    { id: 'food', label: '먹을거리' },
    { id: 'item', label: '아이템' },
    { id: 'goods', label: '물품' },
    { id: 'gacha', label: '뽑기' }
  ]);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [newUsageTypeLabel, setNewUsageTypeLabel] = useState('');

  useEffect(() => {
    if (currentUser) {
      console.log('currentUser role:', currentUser.role);
      const currentTeacher = convertUserToTeacher(currentUser);
      setCurrentTeacher(currentTeacher);
      if (currentTeacher) {
        console.log('teacher role after setting:', currentTeacher.role);
        // 인증 정보 확인
        console.log('인증 정보:', {
          uid: currentUser.uid,
          schoolId: currentUser.schoolId,
          userRole: currentUser.role,
          email: currentUser.email,
          teacherRole: currentTeacher.role,
          teacherSchoolId: currentTeacher.schoolId
        });
      }
    }
  }, [currentUser]);

  // 탭·페이지당 개수 변경 시 페이지 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, itemsPerPage, selectedStoreType, selectedCategory]);

  // URL ?store= 과 상점 진입 상태 동기화 (없으면 허브)
  useEffect(() => {
    const fromUrl = parseStoreTypeParam(searchParams.get('store'));
    if (fromUrl !== selectedStoreType) {
      setSelectedStoreType(fromUrl);
      setSelectedCategory('all');
      setSearchTerm('');
      setSelectedStock('all');
      setSelectedActiveStatus('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- URL이 외부에서 바뀔 때만 반영
  }, [searchParams]);

  const handleEnterStore = (storeType: StoreTypeId) => {
    setSelectedStoreType(storeType);
    setSelectedCategory('all');
    setSearchTerm('');
    setSelectedStock('all');
    setSelectedActiveStatus('all');
    setCurrentPage(1);
    setActiveTab('items');
    const next = new URLSearchParams(searchParams);
    next.set('store', storeTypeToQueryValue(storeType));
    setSearchParams(next, { replace: true });
  };

  const handleBackToStoreHub = () => {
    setSelectedStoreType(null);
    setSelectedCategory('all');
    setSearchTerm('');
    setSelectedStock('all');
    setSelectedActiveStatus('all');
    setCurrentPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete('store');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!currentTeacher || !canManageStore(currentTeacher)) return;

    try {
      setLoading(true);
      let classItemsLoaded = false;
      let subjectItemsLoaded = false;
      
      let unsubscribeClass = () => {};
      let unsubscribeSubject = () => {};
      
      const checkLoadingComplete = () => {
        if (classItemsLoaded && subjectItemsLoaded) {
          setLoading(false);
        }
      };
      
      // 학급 아이템 가져오기 (classId가 있을 때만)
      if (currentTeacher.classId) {
        const classStoreRef = collection(db, 'schools', currentTeacher.schoolId, 'classes', currentTeacher.classId, 'storeItems');
        const classQuery = query(
          classStoreRef,
          orderBy('createdAt', 'desc')
        );

        // 학급 아이템 리스너 
        unsubscribeClass = onSnapshot(classQuery, (classSnapshot) => {
          console.log('학급 아이템 스냅샷:', classSnapshot.docs.length);
          const classItems = classSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
            id: doc.id,
              ...data,
              createdAt: convertTimestampToDate(data.createdAt),
              updatedAt: convertTimestampToDate(data.updatedAt)
            };
          }) as StoreItem[];
        
          // 현재 상태에서 교과 아이템만 유지하고 학급 아이템 업데이트
          setItems(prevItems => {
            const subjectItems = prevItems.filter(item => item.targetType === 'subject');
            return [...classItems, ...subjectItems];
          });
          classItemsLoaded = true;
          checkLoadingComplete();
        }, (err) => {
          console.error('Error fetching class items:', err);
          setError('학급 아이템 목록을 불러오는 중 오류가 발생했습니다.');
          classItemsLoaded = true;
          checkLoadingComplete();
        });
      } else {
        classItemsLoaded = true;
        checkLoadingComplete();
      }

      // 교과 아이템 가져오기
      if (!currentTeacher.subjectId) {
        console.log('교과 정보가 없습니다.');
        subjectItemsLoaded = true;
        checkLoadingComplete();
        return;
      }

      // 권한 체크
      if (!canManageStore(currentTeacher)) {
        console.log('아이템 관리 권한이 없습니다.');
        setError('아이템 관리 권한이 없습니다.');
        subjectItemsLoaded = true;
        checkLoadingComplete();
        return;
      }
      
      if(currentTeacher.subjectId){
        const subjectStoreRef = collection(db, 'schools', currentTeacher.schoolId, 'subjects', currentTeacher.subjectId, 'subjectstoreItems');
        const subjectQuery = query(
          subjectStoreRef,
          orderBy('createdAt', 'desc')
        );
        console.log('교과 아이템 쿼리:', subjectQuery);

        // 교과 아이템 리스너
        unsubscribeSubject = onSnapshot(subjectQuery, (subjectSnapshot) => {
          console.log('교과 아이템 스냅샷:', subjectSnapshot.docs.length);
          const subjectItems = subjectSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
            id: doc.id,
              ...data,
              createdAt: convertTimestampToDate(data.createdAt),
              updatedAt: convertTimestampToDate(data.updatedAt)
            };
          }) as StoreItem[];

          // 현재 상태에서 학급 아이템만 유지하고 교과 아이템 업데이트
          setItems(prevItems => {
            const classItems = prevItems.filter(item => item.targetType === 'class');
            return [...classItems, ...subjectItems];
          });
          subjectItemsLoaded = true;
          checkLoadingComplete();
        }, (err) => {
          console.error('Error fetching subject items:', err);
          setError('교과 아이템 목록을 불러오는 중 오류가 발생했습니다.');
          subjectItemsLoaded = true;
          checkLoadingComplete();
        });
      }
      console.log('교과 아이템 쿼리 설정:', {
        schoolId: currentTeacher.schoolId,
        teacherId: currentTeacher.uid,
        subjectId: currentTeacher.subjectId
      });

      // 두 리스너 모두 정리
      return () => {
        unsubscribeClass();
        unsubscribeSubject();
      };
    } catch (err) {
      console.error('Error setting up listeners:', err);
      setError('아이템 목록을 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    }
  }, [currentTeacher]);

  useEffect(() => {
    if (!currentTeacher || !canManageStore(currentTeacher)) return;

    try {
      let classNoticesLoaded = false;
      let subjectNoticesLoaded = false;
      
      let unsubscribeClass = () => {};
      let unsubscribeSubject = () => {};
      
      const checkLoadingComplete = () => {
        if (classNoticesLoaded && subjectNoticesLoaded) {
          setLoading(false);
        }
      };
      
      // 학급 공지사항 가져오기
      if (currentTeacher.classId) {
        const classNoticeRef = collection(db, 'schools', currentTeacher.schoolId, 'classes', currentTeacher.classId, 'storeNotices');
        const classQuery = query(
          classNoticeRef,
          orderBy('createdAt', 'desc')
        );

        unsubscribeClass = onSnapshot(classQuery, (classSnapshot) => {
          const classNotices = classSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
            startDate: doc.data().startDate?.toDate() || undefined,
            endDate: doc.data().endDate?.toDate() || undefined
          })) as StoreNotice[];
        
          setNotices(prevNotices => {
            const subjectNotices = prevNotices.filter(notice => notice.targetType === 'subject');
            return [...classNotices, ...subjectNotices];
          });
          classNoticesLoaded = true;
          checkLoadingComplete();
        });
      } else {
        classNoticesLoaded = true;
        checkLoadingComplete();
      }

      // 교과 공지사항 가져오기
      if (currentTeacher.subjectId) {
        const subjectNoticeRef = collection(db, 'schools', currentTeacher.schoolId, 'subjects', currentTeacher.subjectId, 'subjectstoreNotices');
        const subjectQuery = query(
          subjectNoticeRef,
          orderBy('createdAt', 'desc')
        );

        unsubscribeSubject = onSnapshot(subjectQuery, (subjectSnapshot) => {
          const subjectNotices = subjectSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
            startDate: doc.data().startDate?.toDate() || undefined,
            endDate: doc.data().endDate?.toDate() || undefined
          })) as StoreNotice[];
        
          setNotices(prevNotices => {
            const classNotices = prevNotices.filter(notice => notice.targetType === 'class');
            return [...classNotices, ...subjectNotices];
          });
          subjectNoticesLoaded = true;
          checkLoadingComplete();
        });
      } else {
        subjectNoticesLoaded = true;
        checkLoadingComplete();
      }

      return () => {
        unsubscribeClass();
        unsubscribeSubject();
      };
    } catch (err) {
      console.error('Error setting up notice listeners:', err);
      setError('공지사항을 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    }
  }, [currentTeacher]);


  const handleFileUpload = async (file: File): Promise<string> => {
    try {
      // Storage 서비스 확인
      if (!storage) {
        const errorMsg = 'Storage service is not available. Please check your Firebase configuration.';
        console.error(errorMsg);
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      
      // 사용자 정보 확인
      if (!currentTeacher?.schoolId) {
        const errorMsg = 'User school ID is not available';
        console.error(errorMsg);
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      
      // 파일 유효성 검사
      if (!file || !(file instanceof File)) {
        const errorMsg = '유효하지 않은 파일입니다.';
        console.error(errorMsg);
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      
      // 파일 크기 제한 (10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        const errorMsg = '파일 크기는 10MB를 초과할 수 없습니다.';
        console.error(errorMsg);
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `schools/${currentTeacher.schoolId}/storeItems/${fileName}`);
      
      // 파일 업로드
      await uploadBytes(storageRef, file);
      
      // 다운로드 URL 가져오기
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      
      // 에러 타입에 따른 메시지 처리
      let errorMessage = '파일 업로드 중 오류가 발생했습니다.';
      
      if (error?.code === 'storage/unauthorized') {
        errorMessage = '파일 업로드 권한이 없습니다. 관리자에게 문의하세요.';
      } else if (error?.code === 'storage/canceled') {
        errorMessage = '파일 업로드가 취소되었습니다.';
      } else if (error?.code === 'storage/unknown') {
        errorMessage = '알 수 없는 오류가 발생했습니다. 페이지를 새로고침하고 다시 시도해주세요.';
      } else if (error?.message?.includes('Access to storage is not allowed')) {
        errorMessage = '스토리지 접근이 차단되었습니다. 브라우저 설정을 확인하거나 페이지를 새로고침해주세요.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };



  const handleDeleteItem = async (itemId: string) => {
    if (!currentTeacher || !canManageStore(currentTeacher)) {
      setError('교사 정보가 올바르지 않습니다.');
      return;
    }

    try {
      // 아이템 삭제 경로 결정
      let itemPath = '';
      const itemToDelete = items.find(item => item.id === itemId);
      
      if (!itemToDelete) {
        throw new Error('삭제할 아이템을 찾을 수 없습니다.');
      }
      
      if (itemToDelete.targetType === 'class') {
        itemPath = `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeItems/${itemId}`;
      } else {
        if (!currentTeacher.subjectId) {
          throw new Error('교과 정보가 없습니다.');
        }
        itemPath = `schools/${currentTeacher.schoolId}/subjects/${currentTeacher.subjectId}/subjectstoreItems/${itemId}`;
      }

      const itemRef = doc(db, itemPath);
      await deleteDoc(itemRef);
      setSuccess('아이템이 삭제되었습니다.');
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('아이템 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleToggleItemStatus = async (itemId: string, currentStatus: boolean) => {
    if (!currentTeacher?.schoolId) {
      setError('교사 정보가 올바르지 않습니다.');
      return;
    }

    try {
      // 아이템 경로 결정
      let itemPath = '';
      const itemToToggle = items.find(item => item.id === itemId);
      
      if (!itemToToggle) {
        throw new Error('상태를 변경할 아이템을 찾을 수 없습니다.');
      }
      
      if (itemToToggle.targetType === 'class') {
        itemPath = `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeItems/${itemId}`;
      } else {
        if (!currentTeacher.subjectId) {
          throw new Error('교과 정보가 없습니다.');
        }
        itemPath = `schools/${currentTeacher.schoolId}/subjects/${currentTeacher.subjectId}/subjectstoreItems/${itemId}`;
      }

      const itemRef = doc(db, itemPath);
      await updateDoc(itemRef, {
        isActive: !currentStatus,
        updatedAt: serverTimestamp()
      });
      setSuccess('아이템 상태가 변경되었습니다.');
    } catch (err) {
      console.error('Error toggling item status:', err);
      setError('아이템 상태 변경 중 오류가 발생했습니다.');
    }
  };


  // 공지사항 생성 (TODO: 다이얼로그에서 사용)

  // 공지사항 수정

  // 공지사항 삭제
  const handleDeleteNotice = async (noticeId: string, targetType: 'class' | 'subject') => {
    if (!currentTeacher?.schoolId) {
      setError('교사 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const noticePath = targetType === 'class'
        ? `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeNotices/${noticeId}`
        : `schools/${currentTeacher.schoolId}/subjects/${currentTeacher.subjectId}/subjectstoreNotices/${noticeId}`;

      const noticeRef = doc(db, noticePath);
      await deleteDoc(noticeRef);
      setSuccess('공지사항이 삭제되었습니다.');
    } catch (err) {
      console.error('Error deleting notice:', err);
      setError('공지사항 삭제 중 오류가 발생했습니다.');
    }
  };

  // 공지사항 상태 토글
  const handleToggleNoticeStatus = async (noticeId: string, targetType: 'class' | 'subject', currentStatus: boolean) => {
    if (!currentTeacher?.schoolId) {
      setError('교사 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const noticePath = targetType === 'class'
        ? `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeNotices/${noticeId}`
        : `schools/${currentTeacher.schoolId}/subjects/${currentTeacher.subjectId}/subjectstoreNotices/${noticeId}`;

      const noticeRef = doc(db, noticePath);
      await updateDoc(noticeRef, {
        isActive: !currentStatus,
        updatedAt: serverTimestamp()
      });
      setSuccess('공지사항 상태가 변경되었습니다.');
    } catch (err) {
      console.error('Error toggling notice status:', err);
      setError('공지사항 상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 새 공지사항 다이얼로그 열기
  const handleCreateNotice = () => {
    setNoticeDialogMode('create');
    setSelectedNotice(null);
    setIsNoticeEditDialogOpen(true);
  };

  // 공지사항 수정 다이얼로그 열기
  const handleEditNotice = (notice: StoreNotice) => {
    setNoticeDialogMode('edit');
    setSelectedNotice(notice);
    setIsNoticeEditDialogOpen(true);
  };

  // 공지사항 저장
  const handleSaveNotice = async (noticeData: {
    title: string;
    content: string;
    tags: string[];
    startDate: string;
    endDate: string;
    targetType: 'class' | 'subject';
  }) => {
    if (!currentTeacher?.schoolId) {
      setError('교사 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const now = new Date();
      const targetType = noticeData.targetType;
      
      let noticeRef;
      if (noticeDialogMode === 'edit' && selectedNotice) {
        const noticePath = targetType === 'class'
          ? `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeNotices/${selectedNotice.id}`
          : `schools/${currentTeacher.schoolId}/subjects/${currentTeacher.subjectId}/subjectstoreNotices/${selectedNotice.id}`;
        noticeRef = doc(db, noticePath);
      } else {
        const noticePath = targetType === 'class'
        ? `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeNotices`
        : `schools/${currentTeacher.schoolId}/subjects/${currentTeacher.subjectId}/subjectstoreNotices`;
        noticeRef = collection(db, noticePath);
      }

      const noticeDoc = {
        title: noticeData.title,
        content: noticeData.content,
        tags: noticeData.tags,
        startDate: Timestamp.fromDate(new Date(noticeData.startDate)),
        endDate: Timestamp.fromDate(new Date(noticeData.endDate)),
        targetType: noticeData.targetType,
        classId: targetType === 'class' ? currentTeacher.classId : '',
        subjectId: targetType === 'subject' ? currentTeacher.subjectId : '',
        teacherId: currentTeacher.uid,
        isActive: true,
        updatedAt: Timestamp.fromDate(now),
        ...(noticeDialogMode === 'create' && {
          createdAt: Timestamp.fromDate(now)
        })
      };

      if (noticeDialogMode === 'edit' && selectedNotice) {
        await updateDoc(noticeRef as DocumentReference, noticeDoc);
        setNotices(prev => prev.map(notice => 
          notice.id === selectedNotice.id 
            ? { 
                ...notice, 
                title: noticeData.title,
                content: noticeData.content,
                tags: noticeData.tags,
                startDate: new Date(noticeData.startDate), 
                endDate: new Date(noticeData.endDate),
                updatedAt: now
              }
            : notice
        ));
      } else {
        const docRef = await addDoc(noticeRef as CollectionReference, noticeDoc);
        const newNotice: StoreNotice = {
          id: docRef.id,
          title: noticeData.title,
          content: noticeData.content,
          tags: noticeData.tags,
          startDate: new Date(noticeData.startDate),
          endDate: new Date(noticeData.endDate),
          createdAt: now,
          updatedAt: now,
        isActive: true,
          targetType: noticeData.targetType,
          classId: targetType === 'class' ? currentTeacher.classId : '',
          subjectId: targetType === 'subject' ? currentTeacher.subjectId : '',
          teacherId: currentTeacher.uid
        };
        setNotices(prev => [newNotice, ...prev]);
      }

      setIsNoticeEditDialogOpen(false);
      setSuccess('공지사항이 저장되었습니다.');
    } catch (error) {
      console.error('공지사항 저장 오류:', error);
      setError('공지사항 저장 중 오류가 발생했습니다.');
    }
  };

  // 새 아이템 다이얼로그 열기
  const handleCreateItem = () => {
    setItemDialogMode('create');
    setSelectedItem(null);
    setIsItemDialogOpen(true);
  };

  // 아이템 수정 다이얼로그 열기
  const handleEditItem = (item: StoreItem) => {
    setItemDialogMode('edit');
    setSelectedItem(item);
    setIsItemDialogOpen(true);
  };

  // 아이템 저장
  const handleSaveItem = async (itemData: {
    name: string;
    description: string;
    itemCategory: 'usage' | 'qr' | 'seatReservation' | 'seatTakeover' | 'collectible' | 'gacha' | 'cosmetic' | 'furniture' | 'roomSurface' | typeof PET_CONSUMABLE_CATEGORY | typeof PET_PLAY_TICKET_CATEGORY;
    petEffect?: PetItemEffect;
    petPlayAction?: PetPlayActionType;
    usageType?: string;
    gachaItems?: Array<{ itemId?: string; itemName: string; count: number; imageUrl?: string; isBlank?: boolean }>;
    gachaAnimationType?: 'scratch' | 'card' | 'maru';
    characterSlot?: string;
    characterOutfitId?: string;
    furnitureKey?: FurnitureKey;
    roomSurfaceKey?: RoomSurfaceKey;
    price: number;
    hasSalePeriod: boolean;
    salePeriodStart?: string;
    salePeriodEnd?: string;
    hasStockLimit: boolean;
    stock: number;
    isLimited: boolean;
    imageUrl?: string;
    isActive: boolean;
  }) => {
    if (!currentTeacher?.schoolId) {
      setError('교사 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const now = new Date();
      const targetType: 'class' | 'subject' = 'class'; // 기본값
      
      let itemRef;
      if (itemDialogMode === 'edit' && selectedItem) {
        const itemPath = targetType === 'class'
          ? `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeItems/${selectedItem.id}`
          : `schools/${currentTeacher.schoolId}/subjects/${currentTeacher.subjectId}/subjectstoreItems/${selectedItem.id}`;
        itemRef = doc(db, itemPath);
      } else {
        const itemPath = targetType === 'class'
          ? `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeItems`
          : `schools/${currentTeacher.schoolId}/subjects/${currentTeacher.subjectId}/subjectstoreItems`;
        itemRef = collection(db, itemPath);
      }

      // undefined 값을 제거하는 헬퍼 함수
      const removeUndefined = (obj: any): any => {
        if (obj === undefined) {
          return undefined; // undefined는 제거됨
        }
        if (obj === null) {
          return null; // null은 Firestore에서 허용됨
        }
        if (Array.isArray(obj)) {
          const cleaned = obj.map(removeUndefined).filter(item => item !== undefined);
          return cleaned.length > 0 ? cleaned : undefined;
        }
        if (typeof obj === 'object' && obj !== null) {
          const cleaned: any = {};
          let hasAnyValue = false;
          for (const key in obj) {
            if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
              const cleanedValue = removeUndefined(obj[key]);
              if (cleanedValue !== undefined) {
                cleaned[key] = cleanedValue;
                hasAnyValue = true;
              }
            }
          }
          return hasAnyValue ? cleaned : undefined;
        }
        return obj;
      };

      // 뽑기 아이템인 경우 재고를 gachaItems의 총 count 합계로 자동 계산
      let calculatedStock = itemData.hasStockLimit ? (itemData.stock || 0) : 0;
      if (itemData.itemCategory === 'gacha' && itemData.gachaItems && itemData.gachaItems.length > 0) {
        calculatedStock = itemData.gachaItems.reduce((sum, gachaItem) => sum + (gachaItem.count ?? 1), 0);
      }

      const itemDoc: any = {
        name: itemData.name || '',
        description: itemData.description || '',
        itemCategory: itemData.itemCategory,
        price: itemData.price || 0,
        isLimited: itemData.isLimited ?? false,
        isUnlimitedStock: !itemData.hasStockLimit,
        stock: calculatedStock,
        targetType: targetType,
        itemType: 'point' as const,
        teacherId: currentTeacher.uid,
        isActive: itemData.isActive ?? true,
        updatedAt: Timestamp.fromDate(now),
      };

      // undefined가 아닌 경우에만 필드 추가
      if (itemData.usageType) {
        itemDoc.usageType = itemData.usageType;
      }
      if (itemData.gachaItems && itemData.gachaItems.length > 0) {
        itemDoc.gachaItems = itemData.gachaItems;
      }
      if (itemData.gachaAnimationType) {
        itemDoc.gachaAnimationType = itemData.gachaAnimationType;
      }
      if (itemData.imageUrl) {
        itemDoc.imageUrl = itemData.imageUrl;
      }
      if (FEATURE_CHARACTER_COSMETIC && itemData.itemCategory === 'cosmetic' && itemData.characterSlot) {
        itemDoc.characterSlot = itemData.characterSlot;
      }
      if (FEATURE_CHARACTER_COSMETIC && itemData.itemCategory === 'cosmetic' && itemData.characterOutfitId) {
        itemDoc.characterOutfitId = itemData.characterOutfitId;
      }
      if (itemData.itemCategory === 'furniture' && itemData.furnitureKey) {
        itemDoc.furnitureKey = itemData.furnitureKey;
      }
      if (itemData.itemCategory === 'roomSurface' && itemData.roomSurfaceKey) itemDoc.roomSurfaceKey = itemData.roomSurfaceKey;
      if (itemData.itemCategory === PET_CONSUMABLE_CATEGORY && itemData.petEffect) {
        itemDoc.petEffect = itemData.petEffect;
      }
      if (itemData.itemCategory === PET_PLAY_TICKET_CATEGORY && itemData.petPlayAction) {
        itemDoc.petPlayAction = itemData.petPlayAction;
      }
      if (itemData.hasSalePeriod && itemData.salePeriodStart && itemData.salePeriodEnd) {
        itemDoc.salePeriod = {
          start: Timestamp.fromDate(new Date(itemData.salePeriodStart)),
          end: Timestamp.fromDate(new Date(itemData.salePeriodEnd))
        };
      }
      if (currentTeacher.classId) {
        itemDoc.classId = currentTeacher.classId;
      }
      if (currentTeacher.subjectId) {
        itemDoc.subjectId = currentTeacher.subjectId;
      }
      if (itemDialogMode === 'create') {
        itemDoc.createdAt = Timestamp.fromDate(now);
      }

      // 최종적으로 undefined 값 제거
      const cleanedItemDoc: any = {};
      for (const key in itemDoc) {
        if (itemDoc.hasOwnProperty(key)) {
          const cleanedValue = removeUndefined(itemDoc[key]);
          if (cleanedValue !== undefined) {
            cleanedItemDoc[key] = cleanedValue;
          }
        }
      }

      if (itemDialogMode === 'edit' && selectedItem) {
        await updateDoc(itemRef as DocumentReference, cleanedItemDoc);
      } else {
        await addDoc(itemRef as CollectionReference, cleanedItemDoc);
      }

      setIsItemDialogOpen(false);
      setSuccess('아이템이 저장되었습니다.');
    } catch (error) {
      console.error('아이템 저장 오류:', error);
      setError('아이템 저장 중 오류가 발생했습니다.');
    }
  };

  // 설정 로드
  const loadSettings = async () => {
    if (!currentTeacher?.schoolId || !currentTeacher?.classId) return;
    
    try {
      const settingsRef = doc(db, `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeSettings`, 'config');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.usageTypes) {
          setUsageTypes(data.usageTypes);
        }
      }
    } catch (error) {
      console.error('설정 로드 오류:', error);
    }
  };

  // 설정 저장
  const saveSettings = async () => {
    if (!currentTeacher?.schoolId || !currentTeacher?.classId) return;
    
    try {
      const settingsRef = doc(db, `schools/${currentTeacher.schoolId}/classes/${currentTeacher.classId}/storeSettings`, 'config');
      await setDoc(settingsRef, {
        usageTypes,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setSuccess('설정이 저장되었습니다.');
    } catch (error) {
      console.error('설정 저장 오류:', error);
      setError('설정 저장 중 오류가 발생했습니다.');
    }
  };

  // 아이템 종류 추가
  const handleAddItemCategory = () => {
    if (!newCategoryLabel.trim()) {
      alert('아이템 종류 이름을 입력해주세요.');
      return;
    }
    
    const newId = newCategoryLabel.toLowerCase().replace(/\s+/g, '');
    if (itemCategories.find(cat => cat.id === newId || cat.value === newId)) {
      alert('이미 존재하는 아이템 종류입니다.');
      return;
    }
    
    setItemCategories([...itemCategories, { id: newId, label: newCategoryLabel.trim(), value: newId }]);
    setNewCategoryLabel('');
  };

  // 아이템 종류 삭제
  const handleDeleteItemCategory = (id: string) => {
    if (window.confirm('이 아이템 종류를 삭제하시겠습니까?')) {
      setItemCategories(itemCategories.filter(cat => cat.id !== id));
    }
  };

  // 사용 유형 추가
  const handleAddUsageType = () => {
    if (!newUsageTypeLabel.trim()) {
      alert('사용 유형 이름을 입력해주세요.');
      return;
    }
    
    const newId = newUsageTypeLabel.toLowerCase().replace(/\s+/g, '');
    if (usageTypes.find(type => type.id === newId)) {
      alert('이미 존재하는 사용 유형입니다.');
      return;
    }
    
    setUsageTypes([...usageTypes, { id: newId, label: newUsageTypeLabel.trim() }]);
    setNewUsageTypeLabel('');
  };

  // 사용 유형 삭제
  const handleDeleteUsageType = (id: string) => {
    if (window.confirm('이 사용 유형을 삭제하시겠습니까?')) {
      setUsageTypes(usageTypes.filter(type => type.id !== id));
    }
  };

  // 설정 탭 활성화 시 설정 로드
  useEffect(() => {
    if (activeTab === 'settings' && currentTeacher) {
      loadSettings();
    }
  }, [activeTab, currentTeacher]);

  useEffect(() => {
    if (!currentTeacher?.schoolId || !currentTeacher.classId) {
      setClassStudents([]);
      setGrantTargetStudentId('');
      return;
    }
    const studentsRef = collection(
      db,
      'schools',
      currentTeacher.schoolId,
      'classes',
      currentTeacher.classId,
      'students'
    );
    const unsub = onSnapshot(
      studentsRef,
      (snap) => {
        const rows = snap.docs
          .map((studentDoc) => {
            const data = studentDoc.data() as Record<string, unknown>;
            return {
              id: studentDoc.id,
              name: (data.name as string) || (data.username as string) || studentDoc.id,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        setClassStudents(rows);
      },
      (err) => {
        console.error('학생 목록 구독 오류:', err);
      }
    );
    return () => unsub();
  }, [currentTeacher?.schoolId, currentTeacher?.classId]);

  // 상점 기록 — 학생 보유(실시간)
  useEffect(() => {
    if (activeTab !== 'records' || recordsSubTab !== 'holdings' || !currentTeacher?.schoolId || !currentTeacher.classId) {
      return;
    }
    setRecordsError(null);
    setRecordsLoading(true);
    const studentsRef = collection(
      db,
      'schools',
      currentTeacher.schoolId,
      'classes',
      currentTeacher.classId,
      'students'
    );
    const unsub = onSnapshot(
      studentsRef,
      (snap) => {
        const rows: StudentHoldingRow[] = [];
        snap.forEach((docSnap) => {
          const d = docSnap.data() as Record<string, unknown>;
          const studentName = (d.name as string) || (d.username as string) || docSnap.id;
          const items = (d.items as Record<string, Record<string, unknown>>) || {};
          Object.entries(items).forEach(([inventoryKey, raw]) => {
            const purchasedAt =
              raw?.purchasedAt && typeof (raw.purchasedAt as Timestamp).toDate === 'function'
                ? (raw.purchasedAt as Timestamp).toDate()
                : null;
            rows.push({
              studentId: docSnap.id,
              studentName,
              inventoryKey,
              storeItemId: (raw?.itemId as string) || '',
              itemName: (raw?.name as string) || '이름 없음',
              quantity: typeof raw?.quantity === 'number' ? raw.quantity : undefined,
              status: raw?.status as string | undefined,
              purchasedAt,
            });
          });
        });
        rows.sort((a, b) => (b.purchasedAt?.getTime() ?? 0) - (a.purchasedAt?.getTime() ?? 0));
        setStudentHoldings(rows);
        setRecordsLoading(false);
      },
      (err) => {
        console.error('학생 보유 목록 구독 오류:', err);
        setRecordsError('학생 보유 목록을 불러오지 못했습니다.');
        setRecordsLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, recordsSubTab, currentTeacher?.schoolId, currentTeacher?.classId]);

  // 상점 기록 — 판매(구매) 로그 purchases
  useEffect(() => {
    if (activeTab !== 'records' || recordsSubTab !== 'purchases' || !currentTeacher?.schoolId || !currentTeacher.classId) {
      return;
    }
    setRecordsError(null);
    setRecordsLoading(true);
    const purchasesRef = collection(
      db,
      'schools',
      currentTeacher.schoolId,
      'classes',
      currentTeacher.classId,
      'purchases'
    );
    const purchasesQuery = query(purchasesRef, orderBy('createdAt', 'desc'), limit(400));
    const unsub = onSnapshot(
      purchasesQuery,
      (snap) => {
        const rows: ClassPurchaseDoc[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<ClassPurchaseDoc, 'id'>),
        }));
        setPurchaseRows(rows);
        setRecordsLoading(false);
      },
      (err) => {
        console.error('판매 기록 구독 오류:', err);
        setRecordsError('판매 기록을 불러오지 못했습니다. (인덱스 또는 권한을 확인하세요)');
        setRecordsLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, recordsSubTab, currentTeacher?.schoolId, currentTeacher?.classId]);

  useEffect(() => {
    setRecordsStudentIdFilter('');
    setRecordsItemNameFilter('');
  }, [recordsSubTab]);

  // 필터링된 아이템 (코스메틱 비활성 시 목록에서 제외)
  const itemsForUi = FEATURE_CHARACTER_COSMETIC
    ? items
    : items.filter((item) => item.itemCategory !== 'cosmetic');
  const effectiveCategory =
    !FEATURE_CHARACTER_COSMETIC && selectedCategory === 'cosmetic' ? 'all' : selectedCategory;
  const categoryFilterOptions = selectedStoreType
    ? getCategoryFilterOptions(selectedStoreType)
    : [];
  const visibleStoreTypes = getVisibleStoreTypes();
  const isStoreHub = activeTab === 'items' && selectedStoreType === null;

  const itemsForCurrentTeacher = itemsForUi.filter((item) => {
    if (!currentTeacher) return false;
    const matchesClass = item.targetType === 'class' && item.classId === currentTeacher.classId;
    const matchesSubject = item.targetType === 'subject' && item.subjectId === currentTeacher.subjectId;
    return matchesClass || matchesSubject;
  });

  const filteredItems = itemsForCurrentTeacher.filter(item => {
    if (!selectedStoreType) return false;

    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStoreType = itemBelongsToStoreType(item.itemCategory, selectedStoreType);
    const matchesCategory =
      effectiveCategory === 'all' ||
      item.itemCategory === effectiveCategory ||
      (effectiveCategory === 'seatReservation' && item.itemCategory === 'seat');
    const matchesStock = selectedStock === 'all' || 
      (selectedStock === 'inStock' && (item.isUnlimitedStock || item.stock > 0)) || 
      (selectedStock === 'outOfStock' && !item.isUnlimitedStock && item.stock === 0);
    const matchesActive = selectedActiveStatus === 'all' ||
      (selectedActiveStatus === 'active' && item.isActive) ||
      (selectedActiveStatus === 'inactive' && !item.isActive);

    return matchesSearch && matchesStoreType && matchesCategory && matchesStock && matchesActive;
  });

  const renderStoreHub = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontFamily: 'pretendard' }}>
        <div
          style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: isHubCompact ? '16px' : '18px',
            fontWeight: 600,
            lineHeight: '150%',
          }}
        >
          관리할 상점을 선택하세요
        </div>
        <div
          style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: isHubCompact ? '12px' : '13px',
            fontWeight: 400,
            lineHeight: '150%',
          }}
        >
          상점마다 아이템을 따로 등록하고 판매 상태를 관리해요.
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isHubCompact
            ? 'repeat(2, minmax(0, 1fr))'
            : `repeat(${Math.min(Math.max(visibleStoreTypes.length, 1), 4)}, minmax(0, 1fr))`,
          gap: isHubCompact ? '12px' : '20px',
        }}
      >
        {visibleStoreTypes.map(({ id, label, description }) => {
          const count = countItemsByStoreType(itemsForCurrentTeacher, id);
          const visual = getStoreTypeVisual(id);
          return (
            <VerticalImageCard
              key={id}
              imageUrl={visual.imageUrl}
              imageAlt={`${label} 대표 이미지`}
              imageFit={visual.imageFit}
              imageBackground={visual.background}
              title={label}
              description={description}
              compact={isHubCompact}
              onClick={() => handleEnterStore(id)}
              actionLabel="관리하기"
              meta={
                <span
                  style={{
                    padding: isHubCompact ? '2px 8px' : '3px 10px',
                    borderRadius: '999px',
                    background: 'var(--Color-orange-50, #FFF1E7)',
                    color: 'var(--Color-text-brand-color, #FF6F0F)',
                    fontSize: isHubCompact ? '12px' : '13px',
                    fontWeight: 600,
                    lineHeight: '150%',
                    whiteSpace: 'nowrap',
                  }}
                >
                  등록 {count}개
                </span>
              }
            />
          );
        })}
      </div>
    </div>
  );

  const renderStoreHeader = () => {
    if (!selectedStoreType) return null;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <button
          type="button"
          onClick={handleBackToStoreHub}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '13px',
            fontFamily: 'pretendard',
            fontWeight: 500,
            lineHeight: '20px',
            width: 'fit-content',
          }}
        >
          <LeftArrowIcon width={16} height={16} color="#989898" />
          상점 선택
        </button>
        <div
          style={{
            color: 'var(--Color-text-strong, #0D0D0D)',
            fontSize: isMobile ? '20px' : '24px',
            fontFamily: 'pretendard',
            fontWeight: 600,
            lineHeight: '150%',
          }}
        >
          {getStoreTypeLabel(selectedStoreType)}
        </div>
      </div>
    );
  };

  const renderCategoryFilters = () => {
    if (categoryFilterOptions.length === 0) return null;
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        {categoryFilterOptions.map(({ key, label }) => {
          const isActive = selectedCategory === key;
          return (
            <div
              key={key}
              style={{
                height: '32px',
                padding: '6px 12px',
                background: isActive ? 'var(--Color-fill-normal, #293044)' : 'var(--Color-fill-white, white)',
                borderRadius: '8px',
                border: isActive ? 'none' : '1px solid var(--Color-line-normal, #F0F0F0)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => setSelectedCategory(key)}
            >
              <span
                style={{
                  color: isActive ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                  fontSize: '12px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '18px',
                }}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);
  
  // 공지사항 페이지네이션 계산
  const noticeTotalPages = Math.ceil(notices.length / itemsPerPage);
  const noticeStartIndex = (currentPage - 1) * itemsPerPage;
  const noticeEndIndex = noticeStartIndex + itemsPerPage;
  const currentNotices = notices.slice(noticeStartIndex, noticeEndIndex);

  // 페이지네이션 함수
  const goToPage = (page: number) => {
    const maxPages = activeTab === 'items' ? totalPages : noticeTotalPages;
    if (page >= 1 && page <= maxPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => {
    const maxPages = activeTab === 'items' ? totalPages : noticeTotalPages;
    setCurrentPage(maxPages);
  };
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  const pageSizeSelector = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
      role="group"
      aria-label="페이지당 표시 개수"
    >
      {[5, 30].map((n) => (
        <button
          type="button"
          key={n}
          onClick={() => setItemsPerPage(n)}
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border:
              itemsPerPage === n
                ? '1px solid var(--Color-fill-brand-color, #FF6F0F)'
                : '1px solid var(--Color-line-normal, #F0F0F0)',
            background:
              itemsPerPage === n ? 'rgba(255, 111, 15, 0.08)' : 'var(--Color-background-normal, white)',
            color:
              itemsPerPage === n
                ? 'var(--Color-text-normal, #161616)'
                : 'var(--Color-text-assistive, #989898)',
            cursor: 'pointer',
            fontSize: '14px',
            fontFamily: 'var(--font-family-primary, pretendard)',
            fontWeight: 500,
            lineHeight: '21px',
          }}
        >
          {n}개 보기
        </button>
      ))}
    </div>
  );

  const formatRecordTimestamp = (value: Timestamp | Date | null | undefined) => {
    if (!value) return '—';
    const d =
      value instanceof Date
        ? value
        : typeof (value as Timestamp).toDate === 'function'
          ? (value as Timestamp).toDate()
          : null;
    if (!d) return '—';
    return d.toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const handleGrantItemToStudent = async (item: StoreItem, targetStudentIdParam?: string): Promise<boolean> => {
    if (!currentTeacher?.schoolId || !currentTeacher.classId) {
      setError('학급 정보가 없어 아이템 지급을 진행할 수 없습니다.');
      return false;
    }
    const resolvedTargetStudentId = targetStudentIdParam ?? grantTargetStudentId;
    if (!resolvedTargetStudentId) {
      setError('지급할 학생을 먼저 선택해주세요.');
      return false;
    }
    const targetStudent = classStudents.find((s) => s.id === resolvedTargetStudentId);
    if (!targetStudent) {
      setError('선택한 학생 정보를 찾지 못했습니다.');
      return false;
    }
    setGrantingItemId(item.id);
    setError(null);
    try {
      const studentRef = doc(
        db,
        'schools',
        currentTeacher.schoolId,
        'classes',
        currentTeacher.classId,
        'students',
        resolvedTargetStudentId
      );
      const itemRef =
        item.targetType === 'subject' && item.subjectId
          ? doc(db, 'schools', currentTeacher.schoolId, 'subjects', item.subjectId, 'subjectstoreItems', item.id)
          : doc(db, 'schools', currentTeacher.schoolId, 'classes', currentTeacher.classId, 'storeItems', item.id);
      let grantedInventoryKey = '';
      let grantedGachaLog: Record<string, unknown> | null = null;
      await runTransaction(db, async (transaction) => {
        const studentSnap = await transaction.get(studentRef);
        if (!studentSnap.exists()) {
          throw new Error('학생 정보를 찾을 수 없습니다.');
        }
        let itemSnap = null;
        if (!item.isUnlimitedStock) {
          itemSnap = await transaction.get(itemRef);
          if (!itemSnap.exists()) {
            throw new Error('아이템 정보를 찾을 수 없습니다.');
          }
          const currentStock = itemSnap.data().stock || 0;
          if (currentStock <= 0) {
            throw new Error('재고가 없습니다.');
          }
        }
        const uniqueKey = `${item.id}_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
        grantedInventoryKey = uniqueKey;
        const studentData = studentSnap.data() as Record<string, unknown>;
        const itemData: Record<string, unknown> = {
          itemId: item.id,
          name: item.name,
          description: item.description,
          type: item.itemType,
          targetType: item.targetType,
          itemCategory: item.itemCategory,
          purchasedAt: serverTimestamp(),
          lastUsedAt: null,
          image: item.imageUrl || 'https://via.placeholder.com/150',
        };
        let selectedGachaItemForStore: any = null;
        if (item.itemCategory === 'gacha' && item.gachaItems && item.gachaItems.length > 0) {
          const totalCount = item.gachaItems.reduce((sum, gachaItem) => sum + (gachaItem.count ?? 1), 0);
          if (totalCount <= 0) {
            throw new Error('지급 가능한 뽑기 아이템 재고가 없습니다.');
          }
          const random = Math.floor(Math.random() * totalCount) + 1;
          let cumulativeCount = 0;
          let selectedGachaItem = item.gachaItems[0];
          for (const gachaItem of item.gachaItems) {
            cumulativeCount += gachaItem.count ?? 1;
            if (random <= cumulativeCount) {
              selectedGachaItem = gachaItem;
              selectedGachaItemForStore = gachaItem;
              break;
            }
          }
          itemData.selectedGachaItem = {
            ...(selectedGachaItem.itemId && { itemId: selectedGachaItem.itemId }),
            itemName: selectedGachaItem.itemName,
            ...(selectedGachaItem.imageUrl && { imageUrl: selectedGachaItem.imageUrl }),
            ...(selectedGachaItem.isBlank !== undefined && { isBlank: selectedGachaItem.isBlank }),
          };
          if (item.gachaAnimationType) {
            itemData.gachaAnimationType = item.gachaAnimationType;
          }
          grantedGachaLog = { ...(itemData.selectedGachaItem as Record<string, unknown>) };
        }
        if (FEATURE_CHARACTER_COSMETIC && item.itemCategory === 'cosmetic' && item.characterSlot) {
          const inv = (studentData.characterInventory as string[] | undefined) || [];
          if (item.isLimited && inv.includes(item.id)) {
            throw new Error('이미 보유한 코스메틱은 다시 지급할 수 없습니다.');
          }
          itemData.characterSlot = item.characterSlot;
          itemData.cosmeticStoreItemId = item.id;
        }
        if (item.itemCategory === PET_PLAY_TICKET_CATEGORY && item.petPlayAction) {
          itemData.petPlayAction = item.petPlayAction;
        }
        const studentUpdate: Record<string, unknown> = {
          [`items.${uniqueKey}`]: itemData,
        };
        if (item.itemCategory === 'seatReservation') {
          studentUpdate.reservationItems = increment(1);
        }
        if (FEATURE_CHARACTER_COSMETIC && item.itemCategory === 'cosmetic' && item.characterSlot) {
          studentUpdate.characterInventory = arrayUnion(item.id);
          studentUpdate[`characterLoadout.${item.characterSlot}`] = item.id;
        }
        transaction.update(studentRef, studentUpdate as any);
        if (!item.isUnlimitedStock && itemSnap) {
          const storeItemData = itemSnap.data();
          const currentStock = storeItemData.stock || 0;
          if (item.itemCategory === 'gacha' && selectedGachaItemForStore && storeItemData.gachaItems) {
            const updatedGachaItems = [...storeItemData.gachaItems];
            let found = false;
            if (selectedGachaItemForStore.itemId) {
              for (let i = 0; i < updatedGachaItems.length; i++) {
                if (updatedGachaItems[i].itemId === selectedGachaItemForStore.itemId) {
                  const newCount = (updatedGachaItems[i].count ?? 1) - 1;
                  if (newCount <= 0) {
                    updatedGachaItems.splice(i, 1);
                  } else {
                    updatedGachaItems[i] = { ...updatedGachaItems[i], count: newCount };
                  }
                  found = true;
                  break;
                }
              }
            }
            if (!found && selectedGachaItemForStore.isBlank) {
              for (let i = 0; i < updatedGachaItems.length; i++) {
                const storeItem = updatedGachaItems[i];
                if (storeItem.isBlank && storeItem.itemName === selectedGachaItemForStore.itemName) {
                  const newCount = (storeItem.count ?? 1) - 1;
                  if (newCount <= 0) {
                    updatedGachaItems.splice(i, 1);
                  } else {
                    updatedGachaItems[i] = { ...storeItem, count: newCount };
                  }
                  found = true;
                  break;
                }
              }
            }
            const totalStoreCount = updatedGachaItems.reduce((sum, gachaItem) => sum + (gachaItem.count ?? 1), 0);
            transaction.update(itemRef, {
              gachaItems: updatedGachaItems,
              stock: totalStoreCount,
              updatedAt: serverTimestamp(),
            });
          } else {
            transaction.update(itemRef, {
              stock: currentStock - 1,
              updatedAt: serverTimestamp(),
            });
          }
        }
      });
      try {
        await appendClassStorePurchaseLog({
          schoolId: currentTeacher.schoolId,
          classId: currentTeacher.classId,
          studentId: targetStudent.id,
          studentName: targetStudent.name,
          storeItemId: item.id,
          itemName: item.name,
          price: 0,
          currency: item.itemType === 'stamp' ? 'stamp' : 'point',
          itemType: item.itemType,
          targetType: item.targetType,
          subjectId: item.subjectId ?? null,
          itemCategory: item.itemCategory,
          inventoryKey: grantedInventoryKey,
          teacherId: currentTeacher.uid,
          selectedGachaItem: grantedGachaLog,
        });
      } catch (logErr) {
        console.error('아이템 지급 기록 저장 실패:', logErr);
      }
      setSuccess(`${targetStudent.name} 학생에게 "${item.name}" 아이템을 지급했습니다.`);
      return true;
    } catch (err) {
      console.error('아이템 지급 오류:', err);
      setError(err instanceof Error ? err.message : '아이템 지급 중 오류가 발생했습니다.');
      return false;
    } finally {
      setGrantingItemId(null);
    }
  };

  const openGrantDialog = (item: StoreItem) => {
    setGrantItem(item);
    setGrantTargetStudentId('');
    setIsGrantDialogOpen(true);
  };

  const closeGrantDialog = () => {
    if (grantingItemId) return;
    setIsGrantDialogOpen(false);
    setGrantItem(null);
    setGrantTargetStudentId('');
  };

  const handleConfirmGrant = async () => {
    if (!grantItem) return;
    const granted = await handleGrantItemToStudent(grantItem);
    if (granted) {
      setIsGrantDialogOpen(false);
      setGrantItem(null);
      setGrantTargetStudentId('');
    }
  };

  const renderStoreRecordsPanel = () => {
    if (!currentTeacher?.classId) {
      return (
        <div
          style={{
            padding: '24px',
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '14px',
            fontFamily: 'var(--font-family-primary, pretendard)',
          }}
        >
          학급이 연결된 교사 계정에서만 상점 기록을 확인할 수 있습니다.
        </div>
      );
    }

    const holdingsFiltered = studentHoldings.filter((r) => {
      if (recordsStudentIdFilter && r.studentId !== recordsStudentIdFilter) return false;
      if (recordsItemNameFilter && r.itemName !== recordsItemNameFilter) return false;
      return true;
    });
    const holdingsGrouped = aggregateStudentHoldingsByStudentAndItem(holdingsFiltered);
    const purchasesFiltered = purchaseRows.filter((r) => {
      if (recordsStudentIdFilter && r.studentId !== recordsStudentIdFilter) return false;
      if (recordsItemNameFilter && (r.itemName || '') !== recordsItemNameFilter) return false;
      return true;
    });

    const studentSelectOptions =
      recordsSubTab === 'holdings'
        ? (() => {
            const m = new Map<string, string>();
            studentHoldings.forEach((r) => {
              m.set(r.studentId, r.studentName);
            });
            return [
              { value: '', label: '전체 학생' },
              ...[...m.entries()]
                .sort((a, b) => a[1].localeCompare(b[1], 'ko'))
                .map(([id, name]) => ({ value: id, label: name })),
            ];
          })()
        : (() => {
            const m = new Map<string, string>();
            purchaseRows.forEach((r) => {
              if (r.studentId) {
                m.set(r.studentId, r.studentName || r.studentId);
              }
            });
            return [
              { value: '', label: '전체 학생' },
              ...[...m.entries()]
                .sort((a, b) => a[1].localeCompare(b[1], 'ko'))
                .map(([id, name]) => ({ value: id, label: name })),
            ];
          })();

    const itemSelectOptions =
      recordsSubTab === 'holdings'
        ? (() => {
            const names = [...new Set(studentHoldings.map((r) => r.itemName))].sort((a, b) =>
              a.localeCompare(b, 'ko')
            );
            return [{ value: '', label: '전체 아이템' }, ...names.map((n) => ({ value: n, label: n }))];
          })()
        : (() => {
            const names = [...new Set(purchaseRows.map((r) => r.itemName || '').filter(Boolean))].sort((a, b) =>
              a.localeCompare(b, 'ko')
            );
            return [{ value: '', label: '전체 아이템' }, ...names.map((n) => ({ value: n, label: n }))];
          })();

    const subTabBtn = (key: 'holdings' | 'purchases', label: string) => (
      <button
        type="button"
        key={key}
        onClick={() => setRecordsSubTab(key)}
        style={{
          padding: '8px 14px',
          borderRadius: '8px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontFamily: 'var(--font-family-primary, pretendard)',
          fontWeight: 500,
          background:
            recordsSubTab === key ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-fill-hover, #F5F5F5)',
          color: recordsSubTab === key ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
        }}
      >
        {label}
      </button>
    );

    return (
      <Card
        padding="16px"
        borderRadius="12px"
        boxShadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
        width="100%"
        alignItems="stretch"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--gap-level-4, 16px)',
            width: '100%',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {subTabBtn('holdings', '학생 보유')}
            {subTabBtn('purchases', '판매 기록')}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              width: '100%',
              alignItems: 'flex-end',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px', minWidth: 0 }}>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--Color-text-assistive, #989898)',
                  fontFamily: 'var(--font-family-primary, pretendard)',
                }}
              >
                학생
              </span>
              <DropdownButton
                options={studentSelectOptions}
                value={recordsStudentIdFilter}
                onChange={setRecordsStudentIdFilter}
                placeholder="전체 학생"
                size="medium"
                width="100%"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px', minWidth: 0 }}>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--Color-text-assistive, #989898)',
                  fontFamily: 'var(--font-family-primary, pretendard)',
                }}
              >
                아이템
              </span>
              <DropdownButton
                options={itemSelectOptions}
                value={recordsItemNameFilter}
                onChange={setRecordsItemNameFilter}
                placeholder="전체 아이템"
                size="medium"
                width="100%"
              />
            </div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--Color-text-assistive, #989898)', lineHeight: '18px' }}>
            판매 기록은 이 기능 반영 이후의 구매부터 저장됩니다. 학생 보유는 현재 인벤토리(사용·승인으로 삭제된 항목은 제외)입니다.
          </div>
          {recordsError ? (
            <div style={{ color: '#c33', fontSize: '13px' }}>{recordsError}</div>
          ) : null}
          {recordsLoading ? (
            <div style={{ color: 'var(--Color-text-assistive, #989898)', fontSize: '14px' }}>불러오는 중…</div>
          ) : null}

          {!recordsLoading && recordsSubTab === 'holdings' ? (
            <div
              style={{
                width: '100%',
                minWidth: 0,
                overflowX: 'auto',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  width: '100%',
                  minWidth: 'min(100%, 520px)',
                  gridTemplateColumns: 'minmax(100px, 1.1fr) minmax(160px, 2.2fr) minmax(88px, 0.9fr) minmax(120px, 1fr)',
                  gap: '8px',
                  padding: '10px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: 'var(--Color-background-main, #F4F6FA)',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--font-family-primary, pretendard)',
                }}
              >
                <span>학생</span>
                <span>아이템</span>
                <span>보유 개수</span>
                <span>최근 구매</span>
              </div>
              {holdingsGrouped.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--Color-text-assistive)', fontSize: '14px' }}>
                  표시할 보유 아이템이 없습니다.
                </div>
              ) : (
                holdingsGrouped.map((r) => (
                  <div
                    key={`${r.studentId}-${r.storeItemId || r.itemName}`}
                    style={{
                      display: 'grid',
                      width: '100%',
                      minWidth: 'min(100%, 520px)',
                      gridTemplateColumns: 'minmax(100px, 1.1fr) minmax(160px, 2.2fr) minmax(88px, 0.9fr) minmax(120px, 1fr)',
                      gap: '8px',
                      padding: '10px 12px',
                      fontSize: '13px',
                      borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
                      fontFamily: 'var(--font-family-primary, pretendard)',
                      color: 'var(--Color-text-normal, #161616)',
                    }}
                  >
                    <span>{r.studentName}</span>
                    <span>{r.itemName}</span>
                    <span>
                      {r.count}건
                      {r.statusSummary ? (
                        <span style={{ color: 'var(--Color-text-assistive, #989898)', fontSize: '12px' }}>
                          {' '}
                          ({r.statusSummary})
                        </span>
                      ) : null}
                    </span>
                    <span>{formatRecordTimestamp(r.lastPurchasedAt)}</span>
                  </div>
                ))
              )}
            </div>
          ) : null}

          {!recordsLoading && recordsSubTab === 'purchases' ? (
            <div
              style={{
                width: '100%',
                minWidth: 0,
                overflowX: 'auto',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  width: '100%',
                  minWidth: 'min(100%, 800px)',
                  gridTemplateColumns: 'minmax(112px, 1.1fr) minmax(80px, 0.95fr) minmax(140px, 1.8fr) minmax(80px, 0.85fr) minmax(56px, 0.55fr) minmax(88px, 0.95fr)',
                  gap: '8px',
                  padding: '10px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: 'var(--Color-background-main, #F4F6FA)',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--font-family-primary, pretendard)',
                }}
              >
                <span>일시</span>
                <span>학생</span>
                <span>아이템</span>
                <span>결제</span>
                <span>대상</span>
                <span>뽑기</span>
              </div>
              {purchasesFiltered.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--Color-text-assistive)', fontSize: '14px' }}>
                  저장된 판매 기록이 없습니다.
                </div>
              ) : (
                purchasesFiltered.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'grid',
                      width: '100%',
                      minWidth: 'min(100%, 800px)',
                      gridTemplateColumns: 'minmax(112px, 1.1fr) minmax(80px, 0.95fr) minmax(140px, 1.8fr) minmax(80px, 0.85fr) minmax(56px, 0.55fr) minmax(88px, 0.95fr)',
                      gap: '8px',
                      padding: '10px 12px',
                      fontSize: '13px',
                      borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
                      fontFamily: 'var(--font-family-primary, pretendard)',
                      color: 'var(--Color-text-normal, #161616)',
                    }}
                  >
                    <span>{formatRecordTimestamp(r.createdAt)}</span>
                    <span>{r.studentName || '—'}</span>
                    <span>{r.itemName || '—'}</span>
                    <span>
                      {r.price ?? '—'}
                      {r.currency === 'stamp' ? ' 도장' : r.currency === 'point' ? ' P' : ''}
                    </span>
                    <span>{r.targetType === 'subject' ? '교과' : '학급'}</span>
                    <span>{r.selectedGachaItem?.itemName || '—'}</span>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
      </Card>
    );
  };

  const renderBulkGrantPanel = () => {
    const grantableItems = itemsForUi.filter((item) => item.isActive);
    const selectedBulkItem = grantableItems.find((item) => item.id === bulkGrantItemId) || null;
    const selectedAllStudents = classStudents.length > 0 && bulkGrantStudentIds.length === classStudents.length;

    const toggleBulkStudent = (studentId: string) => {
      setBulkGrantStudentIds((prev) =>
        prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
      );
    };

    const toggleSelectAllBulkStudents = () => {
      if (selectedAllStudents) {
        setBulkGrantStudentIds([]);
      } else {
        setBulkGrantStudentIds(classStudents.map((s) => s.id));
      }
    };

    const handleBulkGrant = async () => {
      if (!selectedBulkItem) {
        setError('일괄 지급할 아이템을 선택해주세요.');
        return;
      }
      if (bulkGrantStudentIds.length === 0) {
        setError('지급할 학생을 1명 이상 선택해주세요.');
        return;
      }
      if (!selectedBulkItem.isUnlimitedStock && selectedBulkItem.stock < bulkGrantStudentIds.length) {
        setError('선택한 학생 수보다 재고가 부족합니다.');
        return;
      }
      setBulkGrantInProgress(true);
      setError(null);
      let successCount = 0;
      for (const studentId of bulkGrantStudentIds) {
        const ok = await handleGrantItemToStudent(selectedBulkItem, studentId);
        if (ok) {
          successCount += 1;
        }
      }
      setBulkGrantInProgress(false);
      setSuccess(`일괄 지급 완료: ${successCount}/${bulkGrantStudentIds.length}명에게 지급했습니다.`);
    };

    return (
      <Card
        padding="16px"
        borderRadius="12px"
        boxShadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
        width="100%"
        alignItems="stretch"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <div style={{ color: 'var(--Color-text-normal, #161616)', fontSize: '18px', fontWeight: 600 }}>
            일괄 지급
          </div>
          <DropdownButton
            options={[
              { value: '', label: '아이템 선택' },
              ...grantableItems.map((item) => ({ value: item.id, label: item.name })),
            ]}
            value={bulkGrantItemId}
            onChange={setBulkGrantItemId}
            placeholder="아이템 선택"
            size="medium"
            width="100%"
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: 'var(--Color-text-assistive-strong, #4C4C4C)' }}>
              학생 선택 ({bulkGrantStudentIds.length}명)
            </span>
            <Button width="auto" height="30px" variant="line" onClick={toggleSelectAllBulkStudents}>
              {selectedAllStudents ? '전체 해제' : '전체 선택'}
            </Button>
          </div>
          <div
            style={{
              maxHeight: '260px',
              overflowY: 'auto',
              border: '1px solid var(--Color-line-normal, #F0F0F0)',
              borderRadius: '8px',
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {classStudents.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--Color-text-assistive, #989898)' }}>학생이 없습니다.</div>
            ) : (
              classStudents.map((student) => (
                <label
                  key={student.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: 'var(--Color-text-normal, #161616)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={bulkGrantStudentIds.includes(student.id)}
                    onChange={() => toggleBulkStudent(student.id)}
                    disabled={bulkGrantInProgress}
                  />
                  {student.name}
                </label>
              ))
            )}
          </div>
          {selectedBulkItem ? (
            <div style={{ fontSize: '12px', color: 'var(--Color-text-assistive, #989898)' }}>
              선택 아이템 재고: {selectedBulkItem.isUnlimitedStock ? '무제한' : selectedBulkItem.stock}
            </div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              width="140px"
              height="36px"
              variant="submit"
              onClick={handleBulkGrant}
              disabled={!selectedBulkItem || bulkGrantStudentIds.length === 0 || bulkGrantInProgress}
            >
              {bulkGrantInProgress ? '지급 중...' : '일괄 지급 실행'}
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  const renderGrantDialog = () => {
    if (!isGrantDialogOpen || !grantItem) return null;
    const isOutOfStock = !grantItem.isUnlimitedStock && grantItem.stock <= 0;
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200,
          padding: '16px',
        }}
        onClick={closeGrantDialog}
      >
        <Card
          width="100%"
          maxWidth="420px"
          padding="20px"
          borderRadius="12px"
          boxShadow="0px 0px 20px rgba(0, 0, 0, 0.15)"
          style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div
              style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '18px',
                fontFamily: 'pretendard',
                fontWeight: 600,
                lineHeight: '27px',
              }}
            >
              학생에게 아이템 지급
            </div>
            <div
              style={{
                color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                fontSize: '13px',
                fontFamily: 'pretendard',
                fontWeight: 500,
                lineHeight: '19.5px',
              }}
            >
              지급 아이템: {grantItem.name}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'var(--Color-text-assistive, #989898)',
                fontFamily: 'pretendard',
              }}
            >
              지급 대상 학생
            </span>
            <DropdownButton
              options={[
                { value: '', label: '학생 선택' },
                ...classStudents.map((student) => ({ value: student.id, label: student.name })),
              ]}
              value={grantTargetStudentId}
              onChange={setGrantTargetStudentId}
              placeholder="학생 선택"
              size="medium"
              width="100%"
            />
          </div>

          {isOutOfStock ? (
            <div style={{ color: '#c33', fontSize: '12px', fontFamily: 'pretendard' }}>
              재고가 없어 지급할 수 없습니다.
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button width="92px" height="36px" variant="line" onClick={closeGrantDialog} disabled={!!grantingItemId}>
              취소
            </Button>
            <Button
              width="120px"
              height="36px"
              variant="submit"
              onClick={handleConfirmGrant}
              disabled={!grantTargetStudentId || !!grantingItemId || isOutOfStock}
            >
              {grantingItemId ? '지급 중...' : '지급하기'}
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div>로딩 중...</div>
      </div>
    );
  }

  // 모바일 버전
  if (isMobile) {
    return (
      <div style={{ 
        width: '100%',
        padding: '16px',
        paddingBottom: '80px'
      }}>
        {/* 헤더 영역 */}
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '24px',
            fontFamily: 'pretendard',
            fontWeight: '600',
            lineHeight: '36px'
          }}>
            상점
          </div>
          
          {/* QR 스캐너 버튼 */}
          <Button
            width="auto"
            height="36px"
            variant="submit"
            onClick={() => {
              console.log('QR 스캐너 열기');
            }}
            leftIcon={<QrCodeIcon width={14} height={14} color="white" />}
          >
            QR
          </Button>
        </div>

        {/* 탭 네비게이션 */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
        }}>
          <div 
            style={{
              padding: '12px 16px',
              borderBottom: activeTab === 'notice' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => setActiveTab('notice')}
          >
            <span style={{
              color: activeTab === 'notice' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)',
              fontSize: '14px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: '21px'
            }}>
              공지사항
            </span>
          </div>
          
          <div 
            style={{
              padding: '12px 16px',
              borderBottom: activeTab === 'items' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => setActiveTab('items')}
          >
            <span style={{
              color: activeTab === 'items' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)',
              fontSize: '14px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: '21px'
            }}>
              상점 아이템
            </span>
          </div>
          
          <div 
            style={{
              padding: '12px 16px',
              borderBottom: activeTab === 'bulkGrant' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => setActiveTab('bulkGrant')}
          >
            <span style={{
              color: activeTab === 'bulkGrant' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)',
              fontSize: '14px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: '21px'
            }}>
              일괄 지급
            </span>
          </div>

          <div 
            style={{
              padding: '12px 16px',
              borderBottom: activeTab === 'records' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => setActiveTab('records')}
          >
            <span style={{
              color: activeTab === 'records' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)',
              fontSize: '14px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: '21px'
            }}>
              상점 기록
            </span>
          </div>

          <div 
            style={{
              padding: '12px 16px',
              borderBottom: activeTab === 'settings' ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => setActiveTab('settings')}
          >
            <span style={{
              color: activeTab === 'settings' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)',
              fontSize: '14px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: '21px'
            }}>
              설정
            </span>
          </div>
        </div>

        {/* 공지사항 탭 내용 */}
        {activeTab === 'notice' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* 새 공지사항 버튼 */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button
                width="auto"
                height="40px"
                variant="submit"
                onClick={() => handleCreateNotice()}
              >
                새 공지사항
              </Button>
            </div>

            {/* 공지사항 리스트 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {currentNotices.length === 0 ? (
                <div style={{
                  padding: '40px 0',
                  textAlign: 'center',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '400'
                }}>
                  등록된 공지사항이 없습니다.
                </div>
              ) : (
                currentNotices.map((notice, index) => {
                  const now = new Date();
                  const isActive = notice.isActive;
                  const isPending = notice.startDate && new Date(notice.startDate) > now;
                  const isExpired = notice.endDate && new Date(notice.endDate) < now;
                  
                  let status = '공지중';
                  let statusBg = '#FFD2B5';
                  let statusColor = 'var(--Color-text-brand-color, #FF6F0F)';
                  
                  if (isExpired) {
                    status = '공지 종료';
                    statusBg = 'var(--Color-fill-white, white)';
                    statusColor = 'var(--Color-text-assistive, #989898)';
                  } else if (isPending) {
                    status = '공지 예정';
                    statusBg = 'var(--Color-fill-white, white)';
                    statusColor = 'var(--Color-text-normal, #161616)';
                  }

                  return (
                    <Card
                      key={notice.id}
                      padding="16px"
                      borderRadius="12px"
                      boxShadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
                    >
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        {/* 제목 및 상태 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}>
                          <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                          }}>
                            <div style={{
                              color: 'var(--Color-text-normal, #161616)',
                              fontSize: '16px',
                              fontFamily: 'pretendard',
                              fontWeight: '600',
                              lineHeight: '24px'
                            }}>
                              {notice.title}
                            </div>
                            <div style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '12px',
                              fontFamily: 'pretendard',
                              fontWeight: '400',
                              lineHeight: '18px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {notice.content}
                            </div>
                          </div>
                          <div style={{
                            padding: '4px 10px',
                            background: statusBg,
                            borderRadius: '8px',
                            border: statusBg === 'var(--Color-fill-white, white)' ? '1px solid var(--Color-line-normal, #F0F0F0)' : 'none',
                            whiteSpace: 'nowrap'
                          }}>
                            <span style={{
                              color: statusColor,
                              fontSize: '11px',
                              fontFamily: 'pretendard',
                              fontWeight: '500',
                              lineHeight: '16px'
                            }}>
                              {status}
                            </span>
                          </div>
                        </div>

                        {/* 태그 */}
                        {notice.tags && notice.tags.length > 0 && (
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px'
                          }}>
                            {notice.tags.map((tag, tagIndex) => (
                              <div
                                key={tagIndex}
                                style={{
                                  padding: '2px 10px',
                                  background: 'var(--Color-fill-white, white)',
                                  borderRadius: '8px',
                                  border: '1px solid var(--Color-line-normal, #F0F0F0)'
                                }}
                              >
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '11px',
                                  fontFamily: 'pretendard',
                                  fontWeight: '500',
                                  lineHeight: '16px'
                                }}>
                                  {tag}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* 기간 및 날짜 */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <div style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12px',
                            fontFamily: 'pretendard',
                            fontWeight: '400',
                            lineHeight: '18px'
                          }}>
                            공지 기간: {notice.startDate && notice.endDate
                              ? `${notice.startDate.toLocaleDateString?.('ko-KR').replace(/\. /g, '.').slice(0, -1)}~${notice.endDate.toLocaleDateString?.('ko-KR').replace(/\. /g, '.').slice(0, -1)}`
                              : '기간 미설정'}
                          </div>
                          <div style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12px',
                            fontFamily: 'pretendard',
                            fontWeight: '400',
                            lineHeight: '18px'
                          }}>
                            등록일: {notice.createdAt.toLocaleDateString?.('ko-KR').replace(/\. /g, '.').slice(0, -1)}
                          </div>
                        </div>

                        {/* 하단 액션 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: '8px',
                          borderTop: '1px solid var(--Color-line-normal, #F0F0F0)'
                        }}>
                          <ToggleSwitch
                            checked={notice.isActive}
                            onChange={() => handleToggleNoticeStatus(notice.id, notice.targetType, notice.isActive)}
                            size="small"
                          />
                          <div style={{
                            display: 'flex',
                            gap: '16px'
                          }}>
                            <div 
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleEditNotice(notice)}
                            >
                              <EditPencilIcon width={16} height={16} color="#989898" />
                            </div>
                            <div 
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                if (window.confirm('이 공지사항을 삭제하시겠습니까?')) {
                                  handleDeleteNotice(notice.id, notice.targetType);
                                }
                              }}
                            >
                              <TrashIcon width={16} height={16} color="#989898" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>

          </div>
        )}

        {/* 상점 아이템 탭 내용 */}
        {activeTab === 'items' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {isStoreHub ? (
              renderStoreHub()
            ) : (
            <>
            {renderStoreHeader()}
            {/* 검색창 및 새 아이템 생성 버튼 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{
                width: '100%',
                height: '40px',
                padding: '8px 16px',
                background: 'var(--Color-fill-white, white)',
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <SearchIcon width={18} height={18} color="#989898" />
                <input
                  type="text"
                  placeholder="아이템 검색"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: '1',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '13px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '19.5px'
                  }}
                />
              </div>
              
              <Button 
                width="100%"
                height="40px"
                variant="submit"
                onClick={handleCreateItem} 
              >
                새 아이템 생성
              </Button>
            </div>

            {renderCategoryFilters()}

            {/* 재고 필터 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              {[
                { key: 'all', label: '전체' },
                { key: 'inStock', label: '재고 있음' },
                { key: 'outOfStock', label: '재고 없음' }
              ].map(({ key, label }) => (
                <div 
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedStock(key as typeof selectedStock)}
                >
                  <CheckCircleIcon 
                    width={16} 
                    height={16} 
                    color={selectedStock === key ? '#FF6F0F' : '#BABABA'} 
                  />
                  <span style={{
                    color: selectedStock === key ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                    fontSize: '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* 활성화 필터 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              {[
                { key: 'all', label: '전체' },
                { key: 'active', label: '활성화' },
                { key: 'inactive', label: '비활성화' }
              ].map(({ key, label }) => (
                <div 
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedActiveStatus(key as typeof selectedActiveStatus)}
                >
                  <CheckCircleIcon 
                    width={16} 
                    height={16} 
                    color={selectedActiveStatus === key ? '#FF6F0F' : '#BABABA'} 
                  />
                  <span style={{
                    color: selectedActiveStatus === key ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                    fontSize: '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* 아이템 개수 */}
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '16px',
              fontFamily: 'pretendard',
              fontWeight: '600',
              lineHeight: '24px'
            }}>
              총 <span style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)' }}>{filteredItems.length}</span>개의 아이템
            </div>

            {/* 아이템 리스트 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {currentItems.length === 0 ? (
                <div style={{
                  padding: '40px 0',
                  textAlign: 'center',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '400'
                }}>
                  등록된 아이템이 없습니다.
                </div>
              ) : (
                currentItems.map((item) => (
                  <Card
                    key={item.id}
                    padding="0"
                    borderRadius="12px"
                    boxShadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
                    style={{
                      overflow: 'hidden'
                    }}
                  >
                    {/* 아이템 이미지 */}
                    <div style={{
                      width: '100%',
                      height: '180px',
                      background: 'var(--Color-background-sub, #F8F8F8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <span style={{
                          fontSize: '48px',
                          color: '#989898'
                        }}>
                          📦
                        </span>
                      )}
                      
                      {/* 상단 태그 및 아이콘 */}
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        right: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        {item.isLimited && (
                          <div style={{
                            padding: '4px 10px',
                            background: '#FFD2B5',
                            borderRadius: '8px'
                          }}>
                            <span style={{
                              color: 'var(--Color-text-brand-color, #FF6F0F)',
                              fontSize: '11px',
                              fontFamily: 'pretendard',
                              fontWeight: '500',
                              lineHeight: '16px'
                            }}>
                              일회성
                            </span>
                          </div>
                        )}
                        {!item.isLimited && <div />}
                        
                        <div style={{
                          display: 'flex',
                          gap: '12px'
                        }}>
                          <div 
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'rgba(255, 255, 255, 0.9)',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleEditItem(item)}
                          >
                            <EditPencilIcon width={16} height={16} color="#989898" />
                          </div>
                          <div 
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'rgba(255, 255, 255, 0.9)',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              if (window.confirm('이 아이템을 삭제하시겠습니까?')) {
                                handleDeleteItem(item.id);
                              }
                            }}
                          >
                            <TrashIcon width={16} height={16} color="#989898" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 아이템 정보 */}
                    <div style={{
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {/* 제목 및 설명 */}
                      <div>
                        <div style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '18px',
                          fontFamily: 'pretendard',
                          fontWeight: '600',
                          lineHeight: '27px',
                          marginBottom: '8px'
                        }}>
                          {item.name}
                        </div>
                        <div style={{
                          color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                          fontSize: '13px',
                          fontFamily: 'pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {item.description || '설명이 없습니다...'}
                        </div>
                      </div>

                      {/* 기간 및 유형 */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px'
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: '8px'
                        }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12px',
                            fontFamily: 'pretendard',
                            fontWeight: '500',
                            lineHeight: '18px',
                            minWidth: '50px'
                          }}>
                            기간
                          </span>
                          <span style={{
                            color: 'var(--Color-text-normal, #161616)',
                            fontSize: '12px',
                            fontFamily: 'pretendard',
                            fontWeight: '500',
                            lineHeight: '18px'
                          }}>
                            {item.salePeriod 
                              ? `${new Date(item.salePeriod.start).toLocaleDateString('ko-KR').replace(/\. /g, '.').slice(0, -1)}~${new Date(item.salePeriod.end).toLocaleDateString('ko-KR').replace(/\. /g, '.').slice(0, -1)}`
                              : '제한 없음'}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '8px'
                        }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12px',
                            fontFamily: 'pretendard',
                            fontWeight: '500',
                            lineHeight: '18px',
                            minWidth: '50px'
                          }}>
                            유형
                          </span>
                          <span style={{
                            color: 'var(--Color-text-normal, #161616)',
                            fontSize: '12px',
                            fontFamily: 'pretendard',
                            fontWeight: '500',
                            lineHeight: '18px'
                          }}>
                            {item.itemCategory === 'qr' ? 'QR 코드' :
                             item.itemCategory === 'seatReservation' ? '좌석 예약권' :
                             item.itemCategory === 'seatTakeover' ? '좌석 강탈권' :
                             item.itemCategory === 'collectible' ? '소장용' :
                             item.itemCategory === 'gacha' ? '뽑기' :
                             item.itemCategory === PET_CONSUMABLE_CATEGORY ? '캐릭터 간식' :
                             item.itemCategory === PET_PLAY_TICKET_CATEGORY ? '놀아주기 이용권' :
                             item.itemCategory === 'cosmetic' ? '캐릭터 복장' :
                             item.itemCategory === 'furniture' ? '마이홈 가구' :
                             item.itemCategory === 'roomSurface' ? '마이홈 벽지·바닥' :
                             item.itemCategory === 'usage' && item.usageType 
                               ? `${usageTypes.find(type => type.id === item.usageType)?.label || item.usageType}`
                               : '사용권'}
                          </span>
                        </div>
                      </div>

                      {/* 하단 금액/재고/토글 */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '12px',
                        borderTop: '1px solid var(--Color-line-normal, #F0F0F0)'
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: '20px'
                        }}>
                          <div>
                            <div style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '11px',
                              fontFamily: 'pretendard',
                              fontWeight: '500',
                              lineHeight: '16px',
                              marginBottom: '4px'
                            }}>
                              금액
                            </div>
                            <div style={{
                              color: 'var(--Color-text-brand-color, #FF6F0F)',
                              fontSize: '20px',
                              fontFamily: 'pretendard',
                              fontWeight: '600',
                              lineHeight: '30px'
                            }}>
                              {item.price}
                            </div>
                          </div>
                          <div>
                            <div style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '11px',
                              fontFamily: 'pretendard',
                              fontWeight: '500',
                              lineHeight: '16px',
                              marginBottom: '4px'
                            }}>
                              재고
                            </div>
                            <div style={{
                              color: 'var(--Color-text-brand-color, #FF6F0F)',
                              fontSize: '20px',
                              fontFamily: 'pretendard',
                              fontWeight: '600',
                              lineHeight: '30px'
                            }}>
                              {item.isUnlimitedStock ? '-' : (item.stock === 0 ? '없음' : item.stock)}
                            </div>
                          </div>
                        </div>
                        <ToggleSwitch
                          checked={item.isActive}
                          onChange={() => handleToggleItemStatus(item.id, item.isActive)}
                          size="medium"
                        />
                      </div>
                      <Button
                        width="100%"
                        height="36px"
                        variant="line"
                        onClick={() => openGrantDialog(item)}
                        disabled={grantingItemId === item.id}
                      >
                        지급
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>

            </>
            )}
          </div>
        )}

        {/* 설정 탭 내용 */}
        {activeTab === 'settings' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {/* 사용 유형 설정 */}
            <Card
              padding="24px"
              borderRadius="12px"
              boxShadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
            >
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '18px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '27px'
                }}>
                  사용 유형
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {usageTypes.map((type) => (
                    <div
                      key={type.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: 'var(--Color-background-sub, #F8F8F8)',
                        borderRadius: '8px',
                        gap: '12px'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flex: 1
                      }}>
                        {/* 이미지 미리보기 */}
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '8px',
                          background: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          border: '1px solid var(--Color-line-normal, #F0F0F0)'
                        }}>
                          {type.imageUrl ? (
                            <img 
                              src={type.imageUrl} 
                              alt={type.label}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: '24px' }}>📦</span>
                          )}
                        </div>
                      <span style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '14px',
                        fontFamily: 'pretendard',
                        fontWeight: '500',
                        lineHeight: '21px'
                      }}>
                          {type.label}
                      </span>
                    </div>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                        {/* 이미지 업로드 버튼 */}
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          id={`usage-type-image-${type.id}`}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const imageUrl = await handleFileUpload(file);
                              setUsageTypes(usageTypes.map(t => 
                                t.id === type.id ? { ...t, imageUrl } : t
                              ));
                            } catch (error) {
                              console.error('이미지 업로드 실패:', error);
                              alert('이미지 업로드에 실패했습니다.');
                            }
                          }}
                        />
                        <div
                      style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'white',
                            border: '1px solid var(--Color-line-normal, #F0F0F0)',
                        display: 'flex',
                        alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                      }}
                          onClick={() => document.getElementById(`usage-type-image-${type.id}`)?.click()}
                        >
                          <PlusCircleIcon width={20} height={20} color="#989898" />
                        </div>
                      <Button
                        width="auto"
                        height="32px"
                        variant="cancel"
                        onClick={() => handleDeleteUsageType(type.id)}
                      >
                        삭제
                      </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  <InputField
                    value={newUsageTypeLabel}
                    onChange={setNewUsageTypeLabel}
                    placeholder="새 사용 유형 이름"
                    width="300px"
                    height="40px"
                  />
                  <Button
                    width="auto"
                    height="40px"
                    variant="submit"
                    onClick={handleAddUsageType}
                  >
                    추가
                  </Button>
                </div>
              </div>
            </Card>

            {/* 저장 버튼 */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button
                width="auto"
                height="48px"
                variant="submit"
                onClick={saveSettings}
              >
                설정 저장
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'bulkGrant' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', minWidth: 0 }}>
            {renderBulkGrantPanel()}
          </div>
        )}

        {activeTab === 'records' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', minWidth: 0 }}>
            {renderStoreRecordsPanel()}
          </div>
        )}

        {/* 공지사항 편집 다이얼로그 */}
        <EditStoreNoticeDialog
          isOpen={isNoticeEditDialogOpen}
          onClose={() => setIsNoticeEditDialogOpen(false)}
          mode={noticeDialogMode}
          initialData={selectedNotice ? {
            id: selectedNotice.id,
            title: selectedNotice.title,
            content: selectedNotice.content,
            tags: selectedNotice.tags || [],
            startDate: selectedNotice.startDate?.toISOString().split('T')[0] || '',
            endDate: selectedNotice.endDate?.toISOString().split('T')[0] || ''
          } : undefined}
          onSave={handleSaveNotice}
        />

        {/* 아이템 생성/수정 다이얼로그 */}
        <EditStoreItemDialog
          isOpen={isItemDialogOpen}
          onClose={() => setIsItemDialogOpen(false)}
          mode={itemDialogMode}
          initialData={selectedItem ? {
            id: selectedItem.id,
            name: selectedItem.name,
            description: selectedItem.description,
            itemCategory: selectedItem.itemCategory,
            petEffect: selectedItem.petEffect,
            petPlayAction: selectedItem.petPlayAction,
            usageType: selectedItem.usageType,
            gachaItems: selectedItem.gachaItems,
            gachaAnimationType: selectedItem.gachaAnimationType,
            price: selectedItem.price,
            hasSalePeriod: !!selectedItem.salePeriod,
            salePeriodStart: selectedItem.salePeriod?.start ? new Date(selectedItem.salePeriod.start).toISOString().split('T')[0] : '',
            salePeriodEnd: selectedItem.salePeriod?.end ? new Date(selectedItem.salePeriod.end).toISOString().split('T')[0] : '',
            hasStockLimit: !selectedItem.isUnlimitedStock,
            stock: selectedItem.stock,
            isLimited: selectedItem.isLimited,
            imageUrl: selectedItem.imageUrl,
            isActive: selectedItem.isActive,
            characterSlot: selectedItem.characterSlot as any,
            characterOutfitId: selectedItem.characterOutfitId as any,
            furnitureKey: selectedItem.furnitureKey
            ,roomSurfaceKey: selectedItem.roomSurfaceKey
          } : undefined}
          onSave={handleSaveItem}
          onFileUpload={handleFileUpload}
          usageTypes={usageTypes}
          storeType={itemDialogMode === 'create' && selectedStoreType ? selectedStoreType : undefined}
          availableItems={items
            .filter(item => item.id !== selectedItem?.id && item.itemCategory !== 'gacha')
            .map(item => ({ id: item.id, name: item.name, imageUrl: item.imageUrl, isActive: item.isActive }))}
        />
        {renderGrantDialog()}
      </div>
    );
  }

  // 데스크톱 버전
  return (
    <div style={{ 
      width: '100%',
      height: '100%'
    }}>
      {/* 헤더 영역 */}
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <div style={{
          flex: '1',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          color: 'var(--Color-text-normal, #161616)',
          fontSize: '32px',
          fontFamily: 'pretendard',
          fontWeight: '600',
          lineHeight: '48px',
          wordWrap: 'break-word'
        }}>
          상점
        </div>
        
        {/* QR 스캐너 버튼 */}
        <Button
          width="118px"
          height="40px"
          variant="submit"
          onClick={() => {
            // TODO: QR 스캐너 기능 구현
            console.log('QR 스캐너 열기');
          }}
          leftIcon={<QrCodeIcon width={16} height={16} color="white" />}
        >
          QR 스캐너
        </Button>
      </div>

      {/* 탭 네비게이션 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* 탭 버튼 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div 
              style={{
                height: '40px',
                padding: '8px 12px',
                background: activeTab === 'notice' ? 'transparent' : 'transparent',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setActiveTab('notice')}
            >
              <span style={{
                color: activeTab === 'notice' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}>
                공지사항
              </span>
            </div>
            
            <div 
              style={{
                height: '40px',
                padding: '8px 12px',
                background: activeTab === 'items' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setActiveTab('items')}
            >
              <span style={{
                color: activeTab === 'items' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}>
                상점 아이템
              </span>
            </div>
            
            <div 
              style={{
                height: '40px',
                padding: '8px 12px',
                background: activeTab === 'bulkGrant' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setActiveTab('bulkGrant')}
            >
              <span style={{
                color: activeTab === 'bulkGrant' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}>
                일괄 지급
              </span>
            </div>

            <div 
              style={{
                height: '40px',
                padding: '8px 12px',
                background: activeTab === 'records' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setActiveTab('records')}
            >
              <span style={{
                color: activeTab === 'records' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}>
                상점 기록
              </span>
            </div>

            <div 
              style={{
                height: '40px',
                padding: '8px 12px',
                background: activeTab === 'settings' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setActiveTab('settings')}
            >
              <span style={{
                color: activeTab === 'settings' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}>
                설정
              </span>
            </div>
          </div>

          {/* 공지사항 탭 내용 */}
          {activeTab === 'notice' && (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
              gap: '20px'
            }}>
              {/* 승인 대기 학생 수 및 새 공지사항 버튼 */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '20px'
              }}>
                {/* 현재 승인 대기 학생 수 */}
                <div style={{
                  flex: '1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '18px',
                      fontFamily: 'pretendard',
                      fontWeight: '600',
                      lineHeight: '27px'
                    }}>
                      현재
                    </span>
                    <span style={{
                      color: 'var(--Color-fill-brand-color, #FF6F0F)',
                      fontSize: '18px',
                      fontFamily: 'pretendard',
                      fontWeight: '600',
                      lineHeight: '27px'
                    }}>
                      {notices.length}
                    </span>
                    <span style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '18px',
                      fontFamily: 'pretendard',
                      fontWeight: '600',
                      lineHeight: '27px'
                    }}>
                      개의 공지사항이 등록되어 있습니다
                    </span>
                  </div>
                </div>
                
                {/* 새 공지사항 버튼 */}
                <Button
                  width="118px"
                  height="40px"
                  variant="submit"
                  onClick={() => handleCreateNotice()}
                >
                  새 공지사항
            </Button>
              </div>

              {/* 공지사항 테이블 */}
              <div style={{
                height: '549px',
                padding: '16px 20px',
                background: 'var(--Color-background-normal, white)',
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {/* 테이블 헤더 */}
                <div style={{
                  width: '100%',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '67px',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      No
                    </div>
                  </div>
                  <div style={{
                    width: '398px',
                    padding: '12px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      공지사항
                    </div>
                  </div>
                  <div style={{
                    width: '268px',
                    padding: '12px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      공지 기간
                    </div>
                  </div>
                  <div style={{
                    width: '175px',
                    padding: '12px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      공지 등록 날짜
                    </div>
                    <div style={{
                      width: '20px',
                      height: '20px',
                  position: 'relative',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        left: '2px',
                        top: '2px',
                      position: 'absolute',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          width: '8.80px',
                          height: '8.80px',
                          left: '3.60px',
                          top: '3.60px',
                          position: 'absolute',
                          background: 'var(--Color-text-assistive, #989898)'
                        }} />
                      </div>
                    </div>
                  </div>
                  <div style={{
                    width: '156px',
                    padding: '12px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      상태
                    </div>
                  </div>
                  <div style={{
                    width: '85px',
                    padding: '12px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      flex: '1',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      공지 노출
                    </div>
                  </div>
                  <div style={{
                    width: '84px',
                    padding: '12px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      flex: '1',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      관리
                    </div>
                  </div>
                </div>

                {/* 테이블 데이터 */}
                <div style={{
                      width: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {notices.length === 0 ? (
                    <div style={{
                      width: '100%',
                      padding: '60px 0',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '400'
                    }}>
                      등록된 공지사항이 없습니다.
                    </div>
                  ) : (
                    notices.map((notice, index) => {
                      // 공지 상태 판단
                      const now = new Date();
                      const isActive = notice.isActive;
                      const isPending = notice.startDate && new Date(notice.startDate) > now;
                      const isExpired = notice.endDate && new Date(notice.endDate) < now;
                      
                      let status = '공지중';
                      let statusBg = '#FFD2B5';
                      let statusColor = 'var(--Color-text-brand-color, #FF6F0F)';
                      
                      if (isExpired) {
                        status = '공지 종료';
                        statusBg = 'var(--Color-fill-white, white)';
                        statusColor = 'var(--Color-text-assistive, #989898)';
                      } else if (isPending) {
                        status = '공지 예정';
                        statusBg = 'var(--Color-fill-white, white)';
                        statusColor = 'var(--Color-text-normal, #161616)';
                      }

                      return (
                        <div 
                          key={notice.id}
                          style={{
                      width: '100%',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{
                            width: '68px',
                            height: '97px',
                            padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                            gap: '2px'
                          }}>
                            <div style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '13px',
                              fontFamily: 'pretendard',
                              fontWeight: '500',
                              lineHeight: '19.50px'
                            }}>
                              {index + 1}
                            </div>
                          </div>
                          <div style={{
                            width: '397px',
                            padding: '12px 8px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              <div style={{
                                color: 'var(--Color-text-normal, #161616)',
                                fontSize: '14px',
                                fontFamily: 'pretendard',
                                fontWeight: '500',
                                lineHeight: '21px'
                              }}>
                                {notice.title}
                              </div>
                              <div style={{
                                color: 'var(--Color-text-assistive, #989898)',
                                fontSize: '12px',
                                fontFamily: 'pretendard',
                                fontWeight: '400',
                                lineHeight: '18px'
                              }}>
                                {notice.content}
                              </div>
                            </div>
                            {notice.tags && notice.tags.length > 0 && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '9px'
                              }}>
                                {notice.tags.map((tag, tagIndex) => (
                                  <div
                                    key={tagIndex}
                                    style={{
                                      padding: '2px 12px',
                                      background: 'var(--Color-fill-white, white)',
                                      borderRadius: '8px',
                                      border: '1px solid var(--Color-line-normal, #F0F0F0)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <div style={{
                                      color: 'var(--Color-text-normal, #161616)',
                                      fontSize: '12px',
                                      fontFamily: 'pretendard',
                                      fontWeight: '500',
                                      lineHeight: '18px'
                                    }}>
                                      {tag}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{
                            width: '268px',
                            height: '97px',
                            padding: '12px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <div style={{
                              color: 'var(--Color-text-normal, #161616)',
                              fontSize: '13px',
                              fontFamily: 'pretendard',
                              fontWeight: '400',
                              lineHeight: '19.50px'
                            }}>
                              {notice.startDate && notice.endDate
                                ? `${notice.startDate.toLocaleDateString?.('ko-KR').replace(/\. /g, '.').slice(0, -1)}~${notice.endDate.toLocaleDateString?.('ko-KR').replace(/\. /g, '.').slice(0, -1)}`
                                : '기간 미설정'
                              }
                            </div>
                          </div>
                          <div style={{
                            width: '175px',
                            height: '97px',
                            padding: '12px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <div style={{
                              color: 'var(--Color-text-normal, #161616)',
                              fontSize: '13px',
                              fontFamily: 'pretendard',
                              fontWeight: '400',
                              lineHeight: '19.50px'
                            }}>
                              {notice.createdAt.toLocaleDateString?.('ko-KR').replace(/\. /g, '.').slice(0, -1)}
                            </div>
                          </div>
                          <div style={{
                            width: '165px',
                            height: '97px',
                            padding: '12px 0px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            gap: '8px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <div style={{
                                padding: '2px 12px',
                                background: statusBg,
                                borderRadius: '8px',
                                border: statusBg === 'var(--Color-fill-white, white)' ? '1px solid var(--Color-line-normal, #F0F0F0)' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <div style={{
                                  color: statusColor,
                                  fontSize: '12px',
                                  fontFamily: 'pretendard',
                                  fontWeight: '500',
                                  lineHeight: '18px'
                                }}>
                                  {status}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '27px'
                          }}>
                            <ToggleSwitch
                              checked={notice.isActive}
                              onChange={() => handleToggleNoticeStatus(notice.id, notice.targetType, notice.isActive)}
                      size="small"
                            />
                            <div style={{
                              width: '68px',
                              height: '97px',
                              padding: '12px 8px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'flex-start',
                              gap: '8px'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}>
                                <div 
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => handleEditNotice(notice)}
                                >
                                  <div style={{
                                    width: '16px',
                                    height: '16px',
                                    left: '2px',
                                    top: '2px',
                                    position: 'absolute',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                  }}>
                                    <EditPencilIcon width={12} height={12} color="#989898" />
                                  </div>
                                </div>
                                <div 
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => {
                                    if (window.confirm('이 공지사항을 삭제하시겠습니까?')) {
                                      handleDeleteNotice(notice.id, notice.targetType);
                                    }
                                  }}
                                >
                                  <div style={{
                                    width: '16px',
                                    height: '16px',
                                    left: '2px',
                                    top: '2px',
                                    position: 'absolute',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                  }}>
                                    <TrashIcon width={12} height={12} color="#989898" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 상점 아이템 탭 내용 */}
          {activeTab === 'items' && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '36px'
            }}>
            {isStoreHub ? (
              renderStoreHub()
            ) : (
            <>
            {renderStoreHeader()}
              {/* 검색 및 필터 영역 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {/* 검색창 및 새 아이템 생성 버튼 */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  {/* 검색창 */}
                  <div style={{
                    width: '1150px',
                    height: '40px',
                    padding: '8px 16px',
                    background: 'var(--Color-fill-white, white)',
                    borderRadius: '999px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <SearchIcon width={20} height={20} color="#989898" />
                    <input
                      type="text"
                      placeholder="아이템 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
                          style={{
                        flex: '1',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '13px',
                        fontFamily: 'pretendard',
                        fontWeight: '400',
                        lineHeight: '19.5px'
                      }}
                    />
                  </div>
                  
                  {/* 새 아이템 생성 버튼 */}
            <Button 
                    width="118px"
                    height="40px"
                    variant="submit"
              onClick={handleCreateItem} 
            >
                    새 아이템 생성
            </Button>
                </div>

                {renderCategoryFilters()}
              </div>

              {/* 아이템 개수 표시 및 재고 필터 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '20px'
                }}>
                  {/* 총 아이템 개수 */}
                  <div style={{
                    flex: '1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '18px',
                        fontFamily: 'pretendard',
                        fontWeight: '600',
                        lineHeight: '27px'
                      }}>
                        총
                      </span>
                      <span style={{
                        color: 'var(--Color-fill-brand-color, #FF6F0F)',
                        fontSize: '18px',
                        fontFamily: 'pretendard',
                        fontWeight: '600',
                        lineHeight: '27px'
                      }}>
                        {filteredItems.length}
                      </span>
                      <span style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '18px',
                        fontFamily: 'pretendard',
                        fontWeight: '600',
                        lineHeight: '27px'
                      }}>
                        개의 아이템이 있습니다
                      </span>
                    </div>
                  </div>
                  
                  {/* 재고 필터 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px'
                  }}>
                    <div 
                          style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedStock('all')}
                    >
                      <CheckCircleIcon 
                        width={16} 
                        height={16} 
                        color={selectedStock === 'all' ? '#FF6F0F' : '#BABABA'} 
                      />
                      <span style={{
                        color: selectedStock === 'all' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                        fontSize: '12px',
                        fontFamily: 'pretendard',
                        fontWeight: '400',
                        lineHeight: '18px'
                      }}>
                        전체
                      </span>
                    </div>
                    
                    <div 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedStock('inStock')}
                    >
                      <CheckCircleIcon 
                        width={16} 
                        height={16} 
                        color={selectedStock === 'inStock' ? '#FF6F0F' : '#BABABA'} 
                      />
                      <span style={{
                        color: selectedStock === 'inStock' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                        fontSize: '12px',
                        fontFamily: 'pretendard',
                        fontWeight: '400',
                        lineHeight: '18px'
                      }}>
                        재고 있음
                      </span>
                    </div>
                    
                    <div 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedStock('outOfStock')}
                    >
                      <CheckCircleIcon 
                        width={16} 
                        height={16} 
                        color={selectedStock === 'outOfStock' ? '#FF6F0F' : '#BABABA'} 
                      />
                      <span style={{
                        color: selectedStock === 'outOfStock' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                        fontSize: '12px',
                        fontFamily: 'pretendard',
                        fontWeight: '400',
                        lineHeight: '18px'
                      }}>
                        재고 없음
                      </span>
                    </div>
                  </div>

                  {/* 활성화 필터 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px'
                  }}>
                    <div 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedActiveStatus('all')}
                    >
                      <CheckCircleIcon 
                        width={16} 
                        height={16} 
                        color={selectedActiveStatus === 'all' ? '#FF6F0F' : '#BABABA'} 
                      />
                      <span style={{
                        color: selectedActiveStatus === 'all' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                        fontSize: '12px',
                        fontFamily: 'pretendard',
                        fontWeight: '400',
                        lineHeight: '18px'
                      }}>
                        전체
                      </span>
                    </div>
                    
                    <div 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedActiveStatus('active')}
                    >
                      <CheckCircleIcon 
                        width={16} 
                        height={16} 
                        color={selectedActiveStatus === 'active' ? '#FF6F0F' : '#BABABA'} 
                      />
                      <span style={{
                        color: selectedActiveStatus === 'active' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                        fontSize: '12px',
                        fontFamily: 'pretendard',
                        fontWeight: '400',
                        lineHeight: '18px'
                      }}>
                        활성화
                      </span>
                    </div>
                    
                    <div 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedActiveStatus('inactive')}
                    >
                      <CheckCircleIcon 
                        width={16} 
                        height={16} 
                        color={selectedActiveStatus === 'inactive' ? '#FF6F0F' : '#BABABA'} 
                      />
                      <span style={{
                        color: selectedActiveStatus === 'inactive' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                        fontSize: '12px',
                        fontFamily: 'pretendard',
                        fontWeight: '400',
                        lineHeight: '18px'
                      }}>
                        비활성화
                      </span>
                    </div>
                  </div>
                </div>

                {/* 아이템 그리드 */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  {currentItems.map((item) => (
                    <div key={item.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <StoreItemCardForTeacher
                        item={item as any as import('../../../components/ui/StoreItemCardForTeacher').StoreItem}
                        isMobile={false}
                        onEdit={(item) => handleEditItem(item as StoreItem)}
                        onDelete={handleDeleteItem}
                        onToggleActive={handleToggleItemStatus}
                        formatDate={(date: Date) => date.toLocaleDateString('ko-KR').replace(/\. /g, '.').slice(0, -1)}
                      />
                      <Button
                        width="243px"
                        height="36px"
                        variant="line"
                        onClick={() => openGrantDialog(item)}
                        disabled={grantingItemId === item.id}
                      >
                        지급
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </>
            )}
            </div>
          )}

          {activeTab === 'bulkGrant' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', minWidth: 0 }}>
              {renderBulkGrantPanel()}
            </div>
          )}
        </div>
      </div>

      {/* 페이지네이션 - 공지사항 */}
      {activeTab === 'notice' && (
        <div style={{
          paddingTop: '20px',
          paddingBottom: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '24px',
          flexWrap: 'wrap',
        }}>
          {pageSizeSelector}
          {/* 맨 처음으로 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div 
              style={{ 
                cursor: currentPage === 1 ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px'
              }}
              onClick={currentPage === 1 ? undefined : goToFirstPage}
            >
              <LeftArrowDoubleIcon 
                width={24} 
                height={24} 
                color={currentPage === 1 ? '#989898' : '#161616'}
              />
            </div>
            <div 
              style={{ 
                cursor: currentPage === 1 ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px'
              }}
              onClick={currentPage === 1 ? undefined : goToPreviousPage}
            >
              <LeftArrowIcon 
                width={24} 
                height={24} 
                color={currentPage === 1 ? '#989898' : '#161616'}
              />
            </div>
          </div>

          {/* 페이지 번호 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {[...Array(Math.max(0, noticeTotalPages))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <div
                  key={pageNum}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => goToPage(pageNum)}
                >
                  <span
                    style={{
                      textAlign: 'center',
                      color:
                        pageNum === currentPage
                          ? 'var(--Color-text-normal, #161616)'
                          : 'var(--Color-text-assistive, #989898)',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '14px',
                    }}
                  >
                    {pageNum}
                  </span>
                </div>
              );
            })}
          </div>

          {/* 다음으로 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                cursor:
                  noticeTotalPages === 0 || currentPage === noticeTotalPages
                    ? 'default'
                    : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
              }}
              onClick={
                noticeTotalPages === 0 || currentPage === noticeTotalPages
                  ? undefined
                  : goToNextPage
              }
            >
              <RightArrowIcon
                width={24}
                height={24}
                color={
                  noticeTotalPages === 0 || currentPage === noticeTotalPages
                    ? '#989898'
                    : '#161616'
                }
              />
            </div>
            <div
              style={{
                cursor:
                  noticeTotalPages === 0 || currentPage === noticeTotalPages
                    ? 'default'
                    : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
              }}
              onClick={
                noticeTotalPages === 0 || currentPage === noticeTotalPages
                  ? undefined
                  : goToLastPage
              }
            >
              <RightArrowDoubleIcon
                width={24}
                height={24}
                color={
                  noticeTotalPages === 0 || currentPage === noticeTotalPages
                    ? '#989898'
                    : '#161616'
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* 설정 탭 내용 */}
      {activeTab === 'settings' && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          {/* 사용 유형 설정 */}
          <Card
            padding="24px"
            borderRadius="12px"
            boxShadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '18px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                lineHeight: '27px'
              }}>
                사용 유형
              </div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {usageTypes.map((type) => (
                  <div
                    key={type.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: 'var(--Color-background-sub, #F8F8F8)',
                      borderRadius: '8px',
                      gap: '12px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flex: 1
                    }}>
                      {/* 이미지 미리보기 */}
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '8px',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: '1px solid var(--Color-line-normal, #F0F0F0)'
                      }}>
                        {type.imageUrl ? (
                          <img 
                            src={type.imageUrl} 
                            alt={type.label}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: '24px' }}>📦</span>
                        )}
                      </div>
                      <span style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '14px',
                        fontFamily: 'pretendard',
                        fontWeight: '500',
                        lineHeight: '21px'
                      }}>
                        {type.label}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center'
                    }}>
                      {/* 이미지 업로드 버튼 */}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        id={`usage-type-image-desktop-${type.id}`}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const imageUrl = await handleFileUpload(file);
                            setUsageTypes(usageTypes.map(t => 
                              t.id === type.id ? { ...t, imageUrl } : t
                            ));
                          } catch (error) {
                            console.error('이미지 업로드 실패:', error);
                            alert('이미지 업로드에 실패했습니다.');
                          }
                        }}
                      />
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: 'white',
                          border: '1px solid var(--Color-line-normal, #F0F0F0)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById(`usage-type-image-desktop-${type.id}`)?.click()}
                      >
                        <PlusCircleIcon width={20} height={20} color="#989898" />
                      </div>
                      <Button
                        width="auto"
                        height="32px"
                        variant="cancel"
                        onClick={() => handleDeleteUsageType(type.id)}
                      >
                        삭제
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}>
                <InputField
                  value={newUsageTypeLabel}
                  onChange={setNewUsageTypeLabel}
                  placeholder="새 사용 유형 이름"
                  width="300px"
                  height="40px"
                />
                <Button
                  width="auto"
                  height="40px"
                  variant="submit"
                  onClick={handleAddUsageType}
                >
                  추가
                </Button>
              </div>
            </div>
          </Card>

          {/* 저장 버튼 */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Button
              width="auto"
              height="48px"
              variant="submit"
              onClick={saveSettings}
            >
              설정 저장
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'records' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', minWidth: 0 }}>
          {renderStoreRecordsPanel()}
        </div>
      )}

      {/* 페이지네이션 - 상점 아이템 */}
      {activeTab === 'items' && selectedStoreType && (
        <div style={{
          paddingTop: '20px',
          paddingBottom: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '24px',
          flexWrap: 'wrap',
        }}>
          {pageSizeSelector}
          {/* 맨 처음으로 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div 
              style={{ 
                cursor: currentPage === 1 ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px'
              }}
              onClick={currentPage === 1 ? undefined : goToFirstPage}
            >
              <LeftArrowDoubleIcon 
                width={24} 
                height={24} 
                color={currentPage === 1 ? '#989898' : '#161616'}
              />
            </div>
            <div 
              style={{ 
                cursor: currentPage === 1 ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px'
              }}
              onClick={currentPage === 1 ? undefined : goToPreviousPage}
            >
              <LeftArrowIcon 
                width={24} 
                height={24} 
                color={currentPage === 1 ? '#989898' : '#161616'}
              />
            </div>
          </div>

          {/* 페이지 번호 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;
              
              return (
                <div
                  key={pageNum}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => goToPage(pageNum)}
                >
                  <span style={{
                    textAlign: 'center',
                    color: pageNum === currentPage 
                      ? 'var(--Color-text-normal, #161616)' 
                      : 'var(--Color-text-assistive, #989898)',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: '14px'
                  }}>
                    {pageNum}
                  </span>
                </div>
              );
            })}
          </div>

          {/* 다음으로 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div 
              style={{ 
                cursor: currentPage === totalPages ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px'
              }}
              onClick={currentPage === totalPages ? undefined : goToNextPage}
            >
              <RightArrowIcon 
                width={24} 
                height={24} 
                color={currentPage === totalPages ? '#989898' : '#161616'}
              />
            </div>
            <div 
              style={{ 
                cursor: currentPage === totalPages ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px'
              }}
              onClick={currentPage === totalPages ? undefined : goToLastPage}
            >
              <RightArrowDoubleIcon 
                width={24} 
                height={24} 
                color={currentPage === totalPages ? '#989898' : '#161616'}
              />
            </div>
          </div>
        </div>
      )}

      {/* 공지사항 편집 다이얼로그 */}
      <EditStoreNoticeDialog
        isOpen={isNoticeEditDialogOpen}
        onClose={() => setIsNoticeEditDialogOpen(false)}
        mode={noticeDialogMode}
        initialData={selectedNotice ? {
          id: selectedNotice.id,
          title: selectedNotice.title,
          content: selectedNotice.content,
          tags: selectedNotice.tags || [],
          startDate: selectedNotice.startDate?.toISOString().split('T')[0] || '',
          endDate: selectedNotice.endDate?.toISOString().split('T')[0] || ''
        } : undefined}
        onSave={handleSaveNotice}
      />

      {/* 아이템 생성/수정 다이얼로그 */}
      <EditStoreItemDialog
        isOpen={isItemDialogOpen}
        onClose={() => setIsItemDialogOpen(false)}
        mode={itemDialogMode}
        initialData={selectedItem ? {
          id: selectedItem.id,
          name: selectedItem.name,
          description: selectedItem.description,
          itemCategory: selectedItem.itemCategory,
          petEffect: selectedItem.petEffect,
          petPlayAction: selectedItem.petPlayAction,
          usageType: selectedItem.usageType,
          gachaItems: selectedItem.gachaItems,
          gachaAnimationType: selectedItem.gachaAnimationType,
          price: selectedItem.price,
          hasSalePeriod: !!selectedItem.salePeriod,
          salePeriodStart: selectedItem.salePeriod?.start ? new Date(selectedItem.salePeriod.start).toISOString().split('T')[0] : '',
          salePeriodEnd: selectedItem.salePeriod?.end ? new Date(selectedItem.salePeriod.end).toISOString().split('T')[0] : '',
          hasStockLimit: !selectedItem.isUnlimitedStock,
          stock: selectedItem.stock,
          isLimited: selectedItem.isLimited,
          imageUrl: selectedItem.imageUrl,
          isActive: selectedItem.isActive,
          characterSlot: selectedItem.characterSlot as any,
          characterOutfitId: selectedItem.characterOutfitId as any,
          furnitureKey: selectedItem.furnitureKey
          ,roomSurfaceKey: selectedItem.roomSurfaceKey
        } : undefined}
        onSave={handleSaveItem}
        onFileUpload={handleFileUpload}
        usageTypes={usageTypes}
        storeType={itemDialogMode === 'create' && selectedStoreType ? selectedStoreType : undefined}
          availableItems={items
          .filter(item => item.id !== selectedItem?.id && item.itemCategory !== 'gacha')
          .map(item => ({ id: item.id, name: item.name, imageUrl: item.imageUrl, isActive: item.isActive }))}
      />
      {renderGrantDialog()}

    </div>
  );
};

export default StoreManagement; 
